import type { MouseEvent } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { timestampFromDate } from '@bufbuild/protobuf/wkt'
import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { BidSegment } from '@ebalance/proto/bfftypes/v1/bid_segment_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import {
  BiddingApprovalMode,
  BiddingApprovalState,
  BiddingBidSendStatus,
  BiddingIntradayOrderStatus,
  BiddingLifecycleAction,
} from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import type { HTMLChakraProps } from '@enechain/ecloud-designsystem'
import {
  AccordionRoot,
  Alert,
  Box,
  Checkbox,
  Flex,
  HStack,
  MenuRoot,
  PrimaryButton,
  AccordionItem as RawAccordionItem,
  AccordionItemContent as RawAccordionItemContent,
  AccordionItemTrigger as RawAccordionItemTrigger,
  MenuContent as RawMenuContent,
  MenuItem as RawMenuItem,
  MenuPositioner as RawMenuPositioner,
  MenuTrigger as RawMenuTrigger,
  Spacer,
  TertiaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'
import {
  FaArrowUpFromBracketRegular,
  FaBoltRegular,
  FaChevronDownRegular,
  FaEllipsisRegular,
  FaPlusRegular,
} from '@enechain/ecloud-icons'

import { IntradayInversionAlert } from '~/components/bidding/IntradayInversionAlert'
import {
  useRegisteredIssuerOptions,
  useRegisteredIssuerOptionsPending,
} from '~/components/bidding/common/IssuerSelector'
import {
  INTRADAY_POSITION_COLORS,
  POSITION_CHART_PLACEHOLDER_HEIGHT,
  PositionChart,
} from '~/components/bidding/common/PositionChart'
import {
  SubmitConfirmModal,
  type SubmitSendTiming,
} from '~/components/bidding/common/SubmitConfirmModal'
import { groupAreasByApprovalMode } from '~/components/bidding/common/approvalModeGroups'
import { buildSelectedAreasLabel } from '~/components/bidding/common/areaLabel'
import { coverageBadgeColorsOf } from '~/components/bidding/common/coverageColors'
import { saveLastBidAreas } from '~/components/bidding/common/lastBidArea'
import { AccordionDisclosureIcon } from '~/components/common/AccordionDisclosureIcon'
import { ActionReasonText } from '~/components/common/ActionReasonText'
import { AlertDismissButton } from '~/components/common/AlertDismissButton'
import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { DsSelect } from '~/components/common/DsSelect'
import type { FilterMultiSelectOption } from '~/components/common/FilterMultiSelect'
import { FilterMultiSelect } from '~/components/common/FilterMultiSelect'
import type { MultiSelectOption } from '~/components/common/MultiSelect'
import { MultiSelect } from '~/components/common/MultiSelect'
import { RichActionMenu } from '~/components/common/RichActionMenu'
import {
  CollapsibleFilterToolbar,
  FilterGrid,
  type FilterGridItem,
  type ToolbarStatusItem,
} from '~/components/common/filters'
import { iconSize } from '~/constants/iconSize'
import { KOMA_COUNT } from '~/constants/koma'
import { useBidLimits } from '~/hooks/bidding/useBidLimits'
import {
  isFeeAdjustedPricePreviewStale,
  isFormulaPricePreviewStale,
  useCreateBiddingIntradayOrder,
} from '~/hooks/bidding/useCreateBiddingIntradayOrder'
import {
  type IntradayOrderRow,
  convertListIntradayResponse,
  useListBiddingIntradayOrders,
} from '~/hooks/bidding/useListBiddingIntradayOrders'
import { useTransitionBiddingIntradayOrderState } from '~/hooks/bidding/useTransitionBiddingIntradayOrderState'
import { useCsvImportLink } from '~/hooks/useCsvImportReturn'
import { useErrorToast } from '~/hooks/useErrorToast'
import { useNowByMinute } from '~/hooks/useNowByMinute'
import { usePersistedFilters } from '~/hooks/usePersistedFilters'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { useWorkflowLockReasons } from '~/hooks/workflowCase/useWorkflowLockReasons'
import { openOrNavigate } from '~/libs/openOrNavigate'
import { appToaster } from '~/libs/toaster'
import { convertAreaLabel } from '~/models/area'
import { now as serverNow } from '~/models/clock'
import { isSameDay, toProtoDate } from '~/models/dateTime'
import { decimalToNumberOr } from '~/models/decimal'
import { komaNumberTimeRange } from '~/models/komaToTime'
import { formatMwSum } from '~/models/mwDisplay'
import { useGetApprovalFlowScreen } from '~/pages/AdminApprovalFlow/hooks/useGetApprovalFlowScreen'
import { IntradayApprovalDialogs } from '~/pages/IntradayBidding/components/IntradayApprovalDialogs'
import { IntradayBidListAreaGate } from '~/pages/IntradayBidding/components/IntradayAreaEmptyState'
import {
  type IntradayBidCreateFormInput,
  IntradayBidCreateModal,
  type IntradayBidCreateResult,
} from '~/pages/IntradayBidding/components/IntradayBidCreateModal'
import { IntradayBidTable } from '~/pages/IntradayBidding/components/IntradayBidTable'
import { IntradayBoardInterconPanel } from '~/pages/IntradayBidding/components/IntradayBoardInterconPanel'
import { IntradayBulkCreateModal } from '~/pages/IntradayBidding/components/IntradayBulkCreateModal'
import { IntradayBulkWithdrawConfirmDialog } from '~/pages/IntradayBidding/components/IntradayBulkWithdrawConfirmDialog'
import {
  INTRADAY_BULK_WITHDRAW_MENU_VALUE,
  IntradayBulkWithdrawMenuItem,
} from '~/pages/IntradayBidding/components/IntradayBulkWithdrawMenuItem'
import { IntradayBulkWithdrawRefusedBanner } from '~/pages/IntradayBidding/components/IntradayBulkWithdrawRefusedBanner'
import { IntradayDeleteConfirmDialog } from '~/pages/IntradayBidding/components/IntradayDeleteConfirmDialog'
import { IntradayOrderDetailModal } from '~/pages/IntradayBidding/components/IntradayOrderDetailModal'
import { IntradaySubmitFailureBanner } from '~/pages/IntradayBidding/components/IntradaySubmitFailureBanner'
import { IntradayWithdrawConfirmDialog } from '~/pages/IntradayBidding/components/IntradayWithdrawConfirmDialog'
import { useIntradayApprovalDecision } from '~/pages/IntradayBidding/hooks/useIntradayApprovalDecision'
import { useIntradayApproverContext } from '~/pages/IntradayBidding/hooks/useIntradayApproverContext'
import { useIntradayBidCreateSubmit } from '~/pages/IntradayBidding/hooks/useIntradayBidCreateSubmit'
import { useIntradayBoardIntercon } from '~/pages/IntradayBidding/hooks/useIntradayBoardIntercon'
import { useIntradayBulkWithdraw } from '~/pages/IntradayBidding/hooks/useIntradayBulkWithdraw'
import { useIntradayDeleteConfirm } from '~/pages/IntradayBidding/hooks/useIntradayDeleteConfirm'
import { useIntradayFeeState } from '~/pages/IntradayBidding/hooks/useIntradayFeeState'
import { useIntradayReturnToDraft } from '~/pages/IntradayBidding/hooks/useIntradayReturnToDraft'
import { useIntradayWithdrawConfirm } from '~/pages/IntradayBidding/hooks/useIntradayWithdrawConfirm'
import { useMarketNotificationStream } from '~/pages/IntradayBidding/hooks/useMarketNotificationStream'
import { useMarketProducts } from '~/pages/IntradayBidding/hooks/useMarketProducts'
import {
  acceptanceStartTimeLabel,
  isBeforeAcceptance,
} from '~/pages/IntradayBidding/models/acceptanceStart'
import type { IntradayAreaEmptyState as AreaEmptyState } from '~/pages/IntradayBidding/models/areaEmptyState'
import {
  type BidListFilterLanding,
  INTRADAY_BID_LIST_FILTERS_STORAGE_KEY,
  type IntradayBidListFilters,
  NO_WORKFLOW,
  STATUS_FILTER_VALUES,
  defaultIntradayBidListFilters,
  includeOpenedWorkflow,
  intradayBidListFiltersCodec,
  pruneUnknownWorkflows,
  widenForCsvImport,
} from '~/pages/IntradayBidding/models/bidListFilters'
import {
  type IntradayBulkCreateConfirmedParams,
  type IntradayBulkCreateOutcome,
  intradayBulkComparableBids,
} from '~/pages/IntradayBidding/models/bulkCreate'
import { convertIntradayBidsFromOrders } from '~/pages/IntradayBidding/models/convertIntradayBidsFromOrders'
import {
  firstOpenSlot,
  gcBoundarySlot,
  gcLineSlot,
  gcStatusOfKoma,
} from '~/pages/IntradayBidding/models/deliveryDate'
import { formatYenPerKwh } from '~/pages/IntradayBidding/models/feeAutoConsider'
import { calcGcAtForKoma } from '~/pages/IntradayBidding/models/gcCountdown'
import type { IntradaySideFilter } from '~/pages/IntradayBidding/models/globalFilters'
import {
  isIntradayApprovalFlowUnresolved,
  resolveIntradayApprovalMode,
} from '~/pages/IntradayBidding/models/intradayApprovalMode'
import {
  buildIntradayAreaBreakdown,
  buildIntradayPositionSlots,
  computeIntradayBidKpi,
  formatAreaDeliveryContractCodes,
  hasIntradayLimitViolation,
  summarizeIntradayCoverage,
} from '~/pages/IntradayBidding/models/intradayBidAggregation'
import {
  intradayBidCreateErrorReason,
  intradayBidCreateExplicitReason,
  translateIntradayBidCreateError,
} from '~/pages/IntradayBidding/models/intradayBidCreateError'
import {
  buildIntradayBidLimitsUnavailableSection,
  buildIntradayJepxViolationSection,
  buildIntradayLimitViolationSection,
} from '~/pages/IntradayBidding/models/intradayBidLimits'
import type { IntradayBidRow } from '~/pages/IntradayBidding/models/intradayBidRow'
import { detectIntradayDeliveryDateInversions } from '~/pages/IntradayBidding/models/intradayDeliveryDateInversion'
import { formulaSummary } from '~/pages/IntradayBidding/models/intradayFormula'
import { buildCreateIntradayOrderRequest } from '~/pages/IntradayBidding/models/intradayOrderWrite'
import type { IntradaySubmitFailure } from '~/pages/IntradayBidding/models/intradaySubmitFailure'
import {
  isIntradaySubmittableBid,
  resolveIntradaySubmitDisabledCause,
} from '~/pages/IntradayBidding/models/intradaySubmitTarget'
import {
  type IntradayBidLimitViolationDetail,
  type IntradayWithdrawAtPassedDetail,
  buildIntradayBidLimitViolationSection,
  buildIntradayRequestWithdrawAtBlockedSection,
  buildIntradayWithdrawAtPassedSection,
  isIntradayWithdrawAtPassed,
  summarizeIntradaySubmitResults,
} from '~/pages/IntradayBidding/models/intradaySubmitViolation'
import { composeIntradayTableStatus } from '~/pages/IntradayBidding/models/intradayTableStatus'
import {
  isKomaStatusLabelBracketed,
  komaStatusLabelText,
  resolveKomaStatusLabel,
} from '~/pages/IntradayBidding/models/komaStatusLabel'
import {
  isMarketClosed,
  marketOpenTimeLabel,
} from '~/pages/IntradayBidding/models/marketOpen'
import {
  buildSuspendedKomaWarningSection,
  suspendedTimeCodesOf,
} from '~/pages/IntradayBidding/models/suspendedKoma'
import { routePaths } from '~/routes/routePaths'

/**
 * chakra v3 の `AccordionItem*Props` は本プロジェクトの型解決 (ecloud 0.14 / chakra 3.29 の
 * d.ts 解決問題) で `HTMLChakraProps` が collapse し `children` / style prop / `value` が
 * 欠落する。 v3 本来の prop シグネチャ (`HTMLChakraProps<element>`) へ cast して復元する。
 * ランタイムは ecloud のまま。
 */
const AccordionItem = RawAccordionItem as React.FC<
  HTMLChakraProps<'div'> & { value: string }
>
const AccordionItemTrigger = RawAccordionItemTrigger as React.FC<
  HTMLChakraProps<'button'>
>
const AccordionItemContent = RawAccordionItemContent as React.FC<
  HTMLChakraProps<'div'>
>
const MenuTrigger = RawMenuTrigger as React.FC<HTMLChakraProps<'button'>>
const MenuContent = RawMenuContent as React.FC<HTMLChakraProps<'div'>>
const MenuPositioner = RawMenuPositioner as React.FC<
  HTMLChakraProps<'div'> & { children?: React.ReactNode }
>
const MenuItem = RawMenuItem as React.FC<
  HTMLChakraProps<'div'> & {
    value: string
    onSelect?: () => void
    onClick?: (e: MouseEvent) => void
  }
>

type Props = {
  /** AC-BIN-111: 発注可能エリアが無い場合、入札一覧だけを原因別の空状態へ差し替える。 */
  areaEmptyState?: AreaEmptyState
  memberBidDisabledReason?: string
  /**
   * page-level エリアフィルタ (= 選択中エリアの実集合)。
   *
   * AC-BIN-018 で単一選択 radio → **複数選択** になった (issue #5442)。
   * **全 9 エリア = 「すべて」** (= 既定値)、 それ以外は選択したエリアの実集合。
   * `IntradayAreaMultiSelect` は widget 上の空選択を全 9 エリアへ正規化するので、
   * **バー経由で空集合が渡ることはない** (0 件表示は AC-BIN-012b / 012c が定義する
   * 状態として下流では引き続き扱える)。
   *
   * 入札一覧は単一 / 複数を問わず選択エリアを横断して表示する。 表示が制限されるのは
   * チャート (AC-BIN-012b) ・札作成 modal の prefill (AC-BIN-002a) ・充足率バッジ
   * (AC-BIN-012c) で、 いずれも「単一エリア選択時のみ」。
   */
  selectedAreas: Set<AreaCode>
  /**
   * チェック中エリアをコマ別合算した need[] (= GetProcurementNeed market=INTRADAY 由来)。
   * undefined = need 取得エラー (needUnavailable と併用、 need=0 で描画)。
   */
  needByKoma?: readonly number[]
  /**
   * need RPC が取得エラーのとき true (= need line 非表示 + 注記。
   * 設計書 bidding-position-need.md §7 採番、恒久 spec 未反映)。
   */
  needUnavailable?: boolean
  /**
   * workflow-back-bar-readonly AC-16: ワークフローケースが完了済み/削除済みのとき true。
   * true のとき注文作成ボタンを disabled にする（表示切替系ではなく編集・確定操作のため）。
   */
  isReadonly?: boolean
  /**
   * 表示中の受渡日 (= page の受渡日 datepicker の値)。 一覧 / need / 札作成 / GC 判定の
   * すべてがこの日を対象にする。
   */
  targetDate: Date
  /**
   * AC-WFW-006a Decision 1: ワークフローケース由来で開いていてケース対象日が未確定
   * (取得中 / 取得失敗) のとき false。 false の間は受渡日で絞る注文一覧を取得しない
   * (= 既定日での先行取得 / polling を避ける)。 省略時は true (= 従来どおり)。
   */
  isTargetDateReady?: boolean
  /**
   * 全体フィルタ (AC-BIN-102) の売買区分。 AC-BIN-018 は「入札一覧のエリア・売/買は
   * 全体フィルターの値をそのまま適用する。 本 AC では再定義しない」と定めるため、
   * ローカル state ではなく親 (`IntradayBidding/index.tsx`) が持つ全体フィルタを props
   * 経由で受け取る (= 約定履歴タブの `ExecutionResultTab` と同じ配線パターン)。
   * 売/買を選ぶ UI は画面の最上部の全体の条件 (`IntradayFilterToolbar`) が持つ。
   */
  side: IntradaySideFilter
  /**
   * 入札一覧の絞り込みを広げる契機 (入札 CSV 取込からの着地 AC-BCI-038・ワークフローから開いた時)。
   * 本タブはタブの切り替えで作り直されるので、 「画面を開いて 1 回だけ」 の判定は親が持ち、
   * まだ当てていない間だけ渡す。 当てたら `onListFilterLandingApplied` で親に知らせる
   */
  listFilterLanding?: BidListFilterLanding
  onListFilterLandingApplied?: () => void
  /** ワークフローから開いている時の、 札が持つワークフローの業務ID (入札一覧の整理で外さない) */
  openedWorkflowId?: string
}

/** 一括作成で一部の札だけ作れた時の結果 (エラーバナーに出す)。 */
type BulkCreateResult = {
  targetDate: Date
  created: number
  gcExcluded: number
  failed: number
  /** 失敗した札の理由 (重複なし)。 */
  reasons: readonly string[]
  /** 理由の分からない失敗が 1 件でもあるか (「時間をおいて再度お試しください」を添える)。 */
  hasUnknownReason: boolean
}

/**
 * ステータスの select の固定幅。
 * DS Select は文字以外に約 89px (左右の余白・区切り線・▼) を使うので、 英語の最長「Withdrawn」 (14px で実測 74px) が
 * 切れない幅に OS のフォント差の余裕 4px を足して 168px だった。 英語の「Auto-canceled」 (自動取消、
 * 14px で約 90px と見積もり。 未実測) は 168px に収まらないので 192px にする (日本語の最長「すべて」「入札中」 は 42px。
 * 120px では「す…」、 160px でも「Withdra…」 と切れていた)
 */
const STATUS_FILTER_WIDTH = '192px'
/**
 * 行の workflowId をフィルタ判定用のキーに正規化する。
 * 未設定・空文字はワークフローを経由せずに作った札 (= NO_WORKFLOW sentinel) として扱う。
 * 選択肢列挙 (workflowIds) と絞り込み判定 (filteredBids) の両方が同じ正規化を
 * 使う必要があるため、 判定条件を 1 箇所にまとめている (どちらかだけ変えると
 * 「ワークフローなし」の対象が食い違う)。
 */
const normalizeWorkflowKey = (workflowId: string | undefined): string =>
  workflowId !== undefined && workflowId !== '' ? workflowId : NO_WORKFLOW

/**
 * 時間前市場「入札作成」 タブを design HTML `intraday.html` `#tab-zaraba` panel に忠実に再現する。
 *
 * 札作成の動線 (= design HTML 準拠):
 * - 「入札追加▽」 ボタン (個別 / 一括 / CSV) に加え、
 *   **ポジション chart のコマをクリック → 札作成 modal が prefill 付きで開く**。
 *   48 コマを常時表示するコマリボン (design 用語) は AC-BIN-002 で廃止対象のため持たない
 *   (絞り込みはコマフィルタで足りる、板発注画面 AC-BIN-025 は対象外で維持)。
 * - prefill: 該当コマの不足/余剰から 売/買 + 数量 (MW)、 page-level エリアを初期値にする
 *   (= design `intradayChartTooltip` の「クリックして札作成 (買 X MW)」 + `onClick` prefill)。
 * - GC 済コマ (= 現在コマ+2 より前) はクリック不可。
 *
 * 札一覧は ListBiddingIntradayOrders RPC の実データから構築 (polling で送信状態を反映)。
 * ポジション chart の need (調達必要量) は親 page の `useProcurementNeedChartData`
 * (GetProcurementNeed market=INTRADAY 由来) から `needByKoma`/`needUnavailable` として渡される。
 */
export const BidCreateTab = ({
  areaEmptyState,
  memberBidDisabledReason,
  selectedAreas,
  needByKoma,
  needUnavailable = false,
  isReadonly = false,
  targetDate,
  isTargetDateReady = true,
  side,
  listFilterLanding,
  onListFilterLandingApplied,
  openedWorkflowId,
}: Props): React.ReactElement => {
  const { t, i18n } = useTranslation()
  // 読み取り専用で非活性にした作成・提出操作の理由 (AC-WFW-014)。画面固有の理由より優先して
  // 単独で出す。取得中・失敗中は reason が undefined で、非活性のまま理由なし。
  const { readonlyReason } = useWorkflowLockReasons()
  const navigate = useNavigate()
  // 取込が終わったら今の画面 (ワークフローの query ごと) へ戻れるよう、 戻り先を付ける
  const csvImportLink = useCsvImportLink(
    routePaths.biddingIntradayCsvImport.path,
  )
  const showSuccessToast = useSuccessToast()
  const showErrorToast = useErrorToast()

  // ----- 札一覧 (= RPC 実データ)。 page と同じ key で dedup される (引数なし list)。 -----
  // design 忠実 UI (PositionChart / IntradayBidTable / aggregation) が消費する
  // IntradayBidRow へ変換する。 polling (refetchInterval 5s) で送信状態の遷移が反映される。
  // page と同じ受渡日フィルタで引く (= 同一 query key で cache を共有する)。
  const targetDateFilter = useMemo(() => toProtoDate(targetDate), [targetDate])
  const { data: listData } = useListBiddingIntradayOrders(
    targetDateFilter,
    undefined,
    { enabled: isTargetDateReady },
  )
  // RPC 実データ (IntradayOrderRow)。 行クリック (AC-007) の詳細モーダルにこの型のまま渡すため
  // design 忠実行 (IntradayBidRow) への変換前の中間表現を保持する。
  const allOrders = useMemo<IntradayOrderRow[]>(
    () => (listData !== undefined ? convertListIntradayResponse(listData) : []),
    [listData],
  )
  const allBids = useMemo<IntradayBidRow[]>(
    () => convertIntradayBidsFromOrders(allOrders),
    [allOrders],
  )
  // 行クリック時に IntradayBidRow.id から対応する IntradayOrderRow を引くための map。
  const ordersById = useMemo(
    () => new Map(allOrders.map((o) => [o.id, o])),
    [allOrders],
  )

  // 承認フロー設定 (= BFF ApprovalFlowService.GetApprovalFlowScreen)。
  // 個別作成・一括作成が送る `approvalMode` の解決と、 提出確認モーダルの確認項目に使う
  // (`resolveIntradayApprovalMode` 参照)。
  // `throwOnError: false` で本タブの主目的 (入札作成・提出) を error boundary に
  // 巻き込まない (= 他の read query と同じ fail-safe 方針)。
  const {
    data: approvalFlowScreenData,
    isLoading: isApprovalFlowScreenLoading,
    isError: isApprovalFlowScreenError,
  } = useGetApprovalFlowScreen({ throwOnError: false })
  // 2026-09-16 決定: 取得中・取得失敗の間は「要承認の入札を自動承認相当
  // (`INTRADAY_APPROVAL_MODE_FALLBACK` = AUTO) として作成できてしまう」 経路を無くす
  // ため、 作成 (個別作成・一括作成) の導線自体を塞ぐ (backend 照合 PR — 対象エリアの
  // 承認フロー設定と送信値が食い違う作成・更新を拒否する — と食い違わないようにする
  // ための codex 指摘 2026-09-15 対応)。 取得失敗のときは案内 Alert で再読み込みを促す。
  // 判定は `isIntradayApprovalFlowUnresolved` に一本化する (= 解決関数のフォールバック
  // 条件と同じ述語を使う。 オフライン時の fetchStatus `paused` で `data === undefined`
  // のまま活性になる窓を塞ぐため、 同 hook を経由する `useIntradayBidCreateSubmit` と
  // 同じ関数を使う)。
  const isApprovalFlowUnavailable = isIntradayApprovalFlowUnresolved({
    processes: approvalFlowScreenData?.processes,
    isLoading: isApprovalFlowScreenLoading,
    isError: isApprovalFlowScreenError,
  })

  /**
   * 個別作成・一括作成モーダルが「選択中のエリアは承認が必要か」 を案内するための解決関数
   * (issue #8312 の PdM 判断、 2026-09-17)。
   *
   * モーダルのエリア選択肢は画面上部のエリアフィルタに縛らない (JEPX の全 9 エリア) ので、
   * 作成するエリアで承認が要るかを**モーダル内の案内**で作成前に伝える。 当初は手順表示
   * (stepper、 フィルタ基準) との食い違いを埋める目的だったが、 手順表示は 2026-10-05 の
   * PdM 判断で廃止した (#8312)。 案内は作成前に承認の要否を伝えるために残す。
   *
   * 案内値と実際に送る `approval_mode` を必ず一致させるため、 判定は書込経路
   * (`useIntradayBidCreateSubmit` / 一括作成の `handleBulkCreateSubmit`) と同じ
   * `resolveIntradayApprovalMode` を同じ入力で呼ぶだけにする (新しい判定を作らない)。
   */
  const resolveApprovalModeForArea = useCallback(
    (areaCode: AreaCode): BiddingApprovalMode =>
      resolveIntradayApprovalMode({
        processes: approvalFlowScreenData?.processes,
        isLoading: isApprovalFlowScreenLoading,
        isError: isApprovalFlowScreenError,
        areaCode,
      }),
    [
      approvalFlowScreenData,
      isApprovalFlowScreenLoading,
      isApprovalFlowScreenError,
    ],
  )

  // ----- 書込 hooks (作成 / 提出) -----
  // 個別作成の確定処理は useIntradayBidCreateSubmit が内部で Create RPC を持つ。
  // 一括作成は同じ Create RPC を対象コマ分ループする (= bulk 専用 RPC は無い)。
  // 個別作成と loading 状態を分けるため別インスタンスにする。
  const { mutateAsync: bulkCreateAsync, isPending: isBulkCreating } =
    useCreateBiddingIntradayOrder()
  // 提出 (= 注文ごとに SUBMIT → backend が outbox enqueue → bid-send worker が
  // pikopiko-jepx(eSLJ) へ送信)。 spot と同型だが intraday は注文単位で SUBMIT する。
  const { mutateAsync: submitAsync, isPending: isSubmitting } =
    useTransitionBiddingIntradayOrderState()

  // ----- 提出確認 modal state -----
  const [submitModalOpen, setSubmitModalOpen] = useState(false)
  // AC-BIN-034a: 提出確認を開く前の取引状態の取り直し中か。
  const [isRefreshingSuspension, setIsRefreshingSuspension] = useState(false)
  // AC-BIN-015: 直近の提出試行で入札制限違反によりブロックされた札の明細
  // (handleSubmitToJepx が SUBMIT RPC の失敗から抽出して詰める)。 次の提出試行
  // 開始時にクリアする (= 古い試行の明細を残さない)。
  const [bidLimitViolations, setBidLimitViolations] = useState<
    IntradayBidLimitViolationDetail[]
  >([])
  // #8317: 直近の提出試行で通った札の件数 (承認申請・予約を含む。 = 違反明細の本文で提出した
  // 件数を示すための値)。 違反した札だけが提出の対象から外れて残りは提出されるため、
  // violation と同じライフサイクル (試行ごとに詰め直し・次の試行開始時にリセット) で持つ。
  const [bidLimitSentCount, setBidLimitSentCount] = useState(0)
  // #10765: 予約送信になった提出の開場時刻 (開場済みの提出では undefined)。
  const [bidLimitReservedOpenTime, setBidLimitReservedOpenTime] = useState<
    string | undefined
  >(undefined)
  // AC-BIN-116: 提出前に外した札と、実行時に backend が外した札を同じ操作結果として保持する。
  // 一覧の再取得後もこの snapshot を使うため、提出結果の明細・件数が変わらない。
  const [withdrawAtPassedBids, setWithdrawAtPassedBids] = useState<
    IntradayWithdrawAtPassedDetail[]
  >([])
  const [submitResultOpen, setSubmitResultOpen] = useState(false)
  // AC-BIN-009: 提出の一部、 または 1 件も通らず時間をおいても通らない失敗がある時のエラーバナー (× で閉じる)。
  const [submitFailure, setSubmitFailure] = useState<
    | {
        targetDate: Date
        passedCount: number
        failures: IntradaySubmitFailure[]
      }
    | undefined
  >(undefined)
  // AC-BIN-116: 送信直前の再判定で時刻を過ぎた承認申請の札。画面の `now` が次の分へ
  // 進む前でも申請ブロックを出すために保持する。
  const [requestWithdrawAtBlockedIds, setRequestWithdrawAtBlockedIds] =
    useState<ReadonlySet<string>>(new Set())

  // ----- filter state -----
  // 売/買は全体フィルタ (props の `side`、 AC-BIN-102) に寄せたため
  // ローカル state は持たない (AC-BIN-018)。 一覧固有の絞り込み (ステータス・ワークフロー・
  // コマ AC-BIN-002・「売買逆転のみ表示」 AC-BIN-046) は他のタブと共有しないが、 AC-SC-031 に従い
  // 全体フィルターとは別の key で保持する。 ワークフローは複数選択 (AC-BIN-018) で空 = 「すべて」
  const { filters: listFilters, updateFilters: updateListFilters } =
    usePersistedFilters<IntradayBidListFilters>({
      storageKey: INTRADAY_BID_LIST_FILTERS_STORAGE_KEY,
      codec: intradayBidListFiltersCodec,
    })
  const {
    status: statusFilter,
    workflows: workflowFilter,
    komas: komaFilter,
    inversionOnly,
  } = listFilters

  // ----- 入札追加 modal state (= コマクリックで prefill 付きで開く) -----
  const [createOpen, setCreateOpen] = useState(false)
  const [createPrefill, setCreatePrefill] = useState<
    Partial<IntradayBidCreateFormInput>
  >({})

  // ----- 一括作成 modal state (= 入札追加▽ メニューから開く、 AC-BIN-006b) -----
  const [bulkOpen, setBulkOpen] = useState(false)
  // 一括作成で一部の札だけ作れた時の結果 (エラーバナー。 AC-BIN-006b)。 受渡日ごとの結果なので
  // 受渡日を変えたら出さない。 次の一括作成の結果で置き換える。
  const [bulkResult, setBulkResult] = useState<BulkCreateResult | undefined>(
    undefined,
  )

  // ----- 取消確認 modal (AC-008) / 詳細・編集モーダル (AC-007) state -----
  // AC-008: 入札行の取消ボタン → 取消確認モーダル (確定で WITHDRAW 状態遷移)。
  const withdrawConfirm = useIntradayWithdrawConfirm()
  const deleteConfirm = useIntradayDeleteConfirm()
  const returnToDraft = useIntradayReturnToDraft()
  const [detailOrder, setDetailOrder] = useState<IntradayOrderRow | undefined>(
    undefined,
  )

  // ----- GC 境界コマ (= このコマより前は締切済) -----
  // 受渡日により変わる: 当日 = 現在コマ + 2 / 未来日 = 全コマ受付中 / 過去日 = 全コマ締切済。
  // 画面を開いたままコマ境界・日付をまたいでも締切状態が固定されないよう、 現在時刻は
  // 1 分ごとに更新される値を使う (= mount 時の new Date() を使い回さない)。
  const now = useNowByMinute()
  const gcSlotIndex = useMemo(
    () => gcBoundarySlot(now, targetDate),
    [now, targetDate],
  )
  // chart / ribbon の GC 基準線は当日のみ引く (別日は「今 GC を迎えるコマ」 が無い)。
  const gcLineIndex = useMemo(
    () => gcLineSlot(now, targetDate),
    [now, targetDate],
  )
  // まだ入札できる最も早いコマ (= 個別作成メニューの初期コマ)。 過去日は undefined = 作成不可。
  const firstOpenKoma = useMemo(
    () => firstOpenSlot(now, targetDate),
    [now, targetDate],
  )

  // ----- 板情報・連系線空容量パネル (AC-BIN-010 / AC-BIN-011) -----
  // design (bidding/intraday.html:341) の対象コマ決定は「コマフィルタで単一コマを
  // 選んでいればそのコマ、 それ以外は次 GC コマ」。 入札追加タブには (design が前提とする)
  // コマ複数選択フィルタ (AC-BIN-002) が未実装のため、 常に後者 (= firstOpenKoma、
  // まだ入札できる最も早いコマ) にフォールバックする。 全コマ GC 済で firstOpenKoma が
  // undefined の間は対象コマが存在しないため取得しない (= パネルは空表示)。
  const boardTargetKoma = firstOpenKoma

  // ----- 予約送信案内 (AC-01/AC-03/AC-17) -----
  // 市場未開場 (受渡日前日 17:00 より前) でも作成・提出は現行どおり可能 (= 導線ガードは
  // 実装しない、bidding-reserved-send 設計書 §4 UI/State)。バナー / モーダル注記は
  // UX 目的の事前案内なので frontend 判定 (marketOpen.ts) で駆動してよいが、実際に
  // 予約になったかどうかの事後通知 (AC-10、提出完了トースト) は RPC response の
  // send_status を SoT にする (§6 Layer 間接続点。ローカル時刻依存の再判定はしない)。
  const marketClosed = isMarketClosed(now, targetDate)
  const marketOpenTime = marketOpenTimeLabel(targetDate, i18n.language)

  // ----- 取引中断中のコマ (AC-BIN-034a) -----
  // 一覧のバッジと提出確認モーダルの警告に使う。 中断中でも作成・編集・提出は止めない。
  const productsEnabled =
    firstOpenKoma !== undefined && isTargetDateReady && !marketClosed
  const {
    data: productsData,
    error: productsError,
    refetch: refetchProducts,
  } = useMarketProducts(targetDateFilter, productsEnabled, true)
  const suspendedTimeCodes = useMemo(
    () => suspendedTimeCodesOf(productsData?.products ?? []),
    [productsData],
  )

  // 板・連系線の取得と NotifyMarket 購読は同じ 1 条件で動かす (#8086 review)。
  const [boardPanelOpen, setBoardPanelOpen] = useState<string[]>([])
  const boardEnabled =
    boardTargetKoma !== undefined &&
    isTargetDateReady &&
    !marketClosed &&
    boardPanelOpen.includes('boardPanel')
  const {
    boardRows,
    totalSellVolume: boardTotalSellVolume,
    totalBuyVolume: boardTotalBuyVolume,
    isLoadingBook: isLoadingBoardBook,
    isErrorBook: isErrorBoardBook,
    errorBook: boardErrorBook,
    interconLines: boardInterconLines,
    interconUpdatedAt: boardInterconUpdatedAt,
    isLoadingIntercon: isLoadingBoardIntercon,
    isErrorIntercon: isErrorBoardIntercon,
    errorIntercon: boardErrorIntercon,
    markStale: markBoardStale,
  } = useIntradayBoardIntercon(
    targetDateFilter,
    boardTargetKoma ?? 1,
    boardEnabled,
  )
  // AC-BIN-011: リアルタイム表示。 板発注タブと同じ NotifyMarket 購読で invalidate する。
  const { streamStopped: boardStreamStopped } = useMarketNotificationStream(
    boardEnabled,
    targetDate,
    boardTargetKoma ?? 0,
  )
  useEffect(() => {
    const failures: Array<[string, { message: string } | null]> = [
      ['板情報', boardErrorBook],
      ['連系線空容量', boardErrorIntercon],
      ['コマ取引状態', productsError],
    ]
    for (const [label, err] of failures) {
      if (err !== null) {
        // eslint-disable-next-line no-console -- 板発注タブと同じ接続不可時の一次切り分け用
        console.error(`[時間前市場] ${label}の取得に失敗:`, err.message)
      }
    }
  }, [boardErrorBook, boardErrorIntercon, productsError])

  // ----- 受付前ガード (AC-BIN-089c) -----
  // 受渡日 D の入札は D-1 12:00 (JST) から受け付ける (D のスポット約定が確定する
  // まで JEPX 渡し契約分を含む需給ギャップが確定しないため)。 受付開始 (12:00) 〜
  // 開場 (17:00) が予約送信の窓なので、 受付前は必ず未開場でもある。
  // frontend 判定は導線ガード用で、 実際の拒否は backend の受付開始ゲートが返す
  // FailedPrecondition (= models/intradayServerError.ts で文言に訳す)。
  const beforeAcceptance = isBeforeAcceptance(now, targetDate)
  const acceptanceStartTime = acceptanceStartTimeLabel(
    targetDate,
    i18n.language,
  )

  // ----- 発注主体 0 件ガード (AC-BIN-056) -----
  // 受渡日 (= タブの受渡日) の広域マスタに自組織の発注主体区分が 1件も登録されて
  // いないと (= 受渡契約コードを解決できる区分が無い)、 札作成モーダルを
  // 開いても [[IssuerSelector]] の空状態案内が出るだけで結局は何も作れない。
  // モーダルを開く前に「個別作成 / 一括作成 / CSVインポート」 の 3 導線を止め、
  // 何をすれば解消するかを示す (#7280 で 3 導線とも対象と確定)。
  //
  // **取得中は 0 件と断定しない**。登録済み区分は画面上部・作成モーダルと同じ
  // `registered_issuer_types` を母集合にする (AC-BIN-004 / AC-BIN-120)。
  // 取得中の空配列をそのまま使うと発注主体が有る組織でもページ描画直後に
  // 警告バナーが一瞬出て 3 導線が一瞬 disabled になる。AC-BIN-056 の条件は
  // 「1件も登録されていない」 であって「まだ取得できていない」 ではないので、
  // 確定するまではブロックせず従来どおりモーダル内の案内に委ねる。
  // 取得失敗は従来どおり 0 件へ縮退する (= モーダル内 [[IssuerSelector]] と同じ扱い)。
  const issuerOptions = useRegisteredIssuerOptions(targetDate, {
    enabled: isTargetDateReady,
  })
  const issuerPending = useRegisteredIssuerOptionsPending(targetDate, {
    enabled: isTargetDateReady,
  })
  const noIssuer = !issuerPending && issuerOptions.length === 0
  const noIssuerMessage = t('page:biddingCommon.issuerSelector.noAccount')

  // ----- area filter -----
  // selectedAreas はチェック中エリアの実集合 (sentinel なし)。 空 = 0 件表示。
  const areaScopedBids = useMemo(
    () => allBids.filter((b) => selectedAreas.has(b.areaCode)),
    [allBids, selectedAreas],
  )

  // ----- ワークフローフィルタの選択肢 (AC-BIN-018) -----
  // 一覧に実在する workflowId を動的に列挙する (= 固定 enum ではないため)。
  // area/side/status とは独立に、 常に allBids 全体から出す (= 他フィルタの選択状態で
  // 選択肢自体が変動して選び直しになる事故を避ける)。
  const workflowIds = useMemo(() => {
    const ids = new Set<string>()
    for (const b of allBids) {
      const key = normalizeWorkflowKey(b.workflowId)
      if (key !== NO_WORKFLOW) {
        ids.add(key)
      }
    }
    return [...ids].sort()
  }, [allBids])
  // FilterMultiSelect の options。 「ワークフローなし」 (NO_WORKFLOW sentinel) を
  // 先頭固定にし、 その後に動的な workflowId を並べる。
  const workflowFilterOptions = useMemo<FilterMultiSelectOption[]>(
    () => [
      {
        value: NO_WORKFLOW,
        label: t('page:biddingIntraday.bidCreate.workflowNone'),
      },
      ...workflowIds.map((id) => ({ value: id, label: id })),
    ],
    [t, workflowIds],
  )

  // 親が渡した着地 (CSV 取込から戻った時・ワークフローから開いた時) で絞り込みを広げる。
  // 契機は渡された時だけ (updateListFilters・callback は毎レンダー新しい参照)。
  useEffect(() => {
    if (listFilterLanding === undefined) return
    if (listFilterLanding.kind === 'csvImport') {
      updateListFilters((prev) => widenForCsvImport(prev, listFilterLanding))
    } else if (
      includeOpenedWorkflow(listFilters, listFilterLanding.workflowId) !==
      undefined
    ) {
      updateListFilters(
        (prev) =>
          includeOpenedWorkflow(prev, listFilterLanding.workflowId) ?? prev,
      )
    }
    onListFilterLandingApplied?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 上記理由
  }, [listFilterLanding])

  // 復元したワークフローのうち受渡日の札に無いものを外す (0 件に見える一覧を作らない)。 当てるのは
  // 復元した時と受渡日を変えた時の、 その日の最初の読み込みが終わった時だけ。 同じ受渡日の polling で
  // 札が消えても外さない (一覧が急に全件になり、 一括板取消 AC-BIN-112 の対象が広がるため)。
  // ケースの対象日が決まるまでは判定しない (既定日の cache の札で外さない。 AC-WFW-006a)。
  const targetTime = targetDate.getTime()
  const prunedTargetTimeRef = useRef<number | undefined>(undefined)
  useEffect(() => {
    if (!isTargetDateReady || listData === undefined) return
    if (prunedTargetTimeRef.current === targetTime) return
    prunedTargetTimeRef.current = targetTime
    if (
      pruneUnknownWorkflows(listFilters, workflowIds, openedWorkflowId) ===
      undefined
    ) {
      return
    }
    updateListFilters(
      (prev) =>
        pruneUnknownWorkflows(prev, workflowIds, openedWorkflowId) ?? prev,
    )
    // listFilters は契機ではない (判定に使うだけ)。 updateListFilters は毎レンダー新しい参照
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 上記理由
  }, [isTargetDateReady, listData, targetTime, workflowIds])

  // ----- コマフィルタの選択肢 (AC-BIN-002 / AC-BIN-054) -----
  // 約定済判定 (AC-BIN-054「当該コマを対象とする自社の札のうち約定量が 0 より大きい
  // ものが 1 枚以上ある場合」) は表示 filter (area/side/status) に依らず全注文
  // (`allOrders`) から見る。 area 等で絞り込んで非表示になった札の約定も
  // 状態ラベルには反映する。
  const matchedKomas = useMemo(() => {
    const s = new Set<number>()
    for (const o of allOrders) {
      if (decimalToNumberOr(o.matchedVolumeMwh) > 0) s.add(o.timeCode)
    }
    return s
  }, [allOrders])

  const komaFilterOptions = useMemo<MultiSelectOption[]>(
    () =>
      Array.from({ length: KOMA_COUNT }, (_, i) => i + 1).map((koma) => {
        const state = resolveKomaStatusLabel(
          now,
          targetDate,
          koma,
          matchedKomas.has(koma),
        )
        const bracket = isKomaStatusLabelBracketed(state)
          ? ` (${komaStatusLabelText(state, t)})`
          : ''
        // 締切間近 (= 5 状態以外の緊急度ヒント) はカッコなしで後置し、状態ラベルと
        // 見た目で区別する (#7603 Des 判断案 B)。 spec (AC-BIN-002 / AC-BSP-004c)
        // は緊急度ヒントを状態ラベルと併記する前提なので、 状態 (state) に関わらず
        // 独立に判定する。 約定済 (matched) は GC 前後に関係なく付くため、
        // 「部分約定のまま GC 間近」 のコマは (約定済) と 締切間近 が両方出る
        // (2026-09-16 レビュー指摘: state === 'open' に限定すると 約定済コマで
        // 締切間近が消え、 まさに残数量の判断が要る局面でヒントが出ない不具合だった)。
        const urgencyHint =
          gcStatusOfKoma(now, targetDate, koma) === 'imminent'
            ? ` ${t('page:biddingIntraday.bidCreate.komaGcNear')}`
            : ''
        return {
          value: String(koma),
          label: `${koma} (${komaNumberTimeRange(koma, '〜')})${bracket}${urgencyHint}`,
        }
      }),
    [now, targetDate, matchedKomas, t],
  )

  // ----- 売/買・ステータス・コマ filter -----
  const filteredBids = useMemo(
    () =>
      areaScopedBids.filter((b) => {
        if (side === 'buy' && b.direction !== Side.BUY) return false
        if (side === 'sell' && b.direction !== Side.SELL) return false
        // 「入札中」 は表示上のステータスで絞る (= bidStatus だけで見ると、 一覧では
        // 「予約中」 と出ている行が「入札中」 に混ざり、 残りが板にある一部約定の札
        // (一覧では「入札中」) が漏れる)。 予約中だけを抜き出す選択肢を足すかは一覧
        // ステータスの区分そのものの話なので #4917 の判断待ち。
        if (
          statusFilter === 'placed' &&
          composeIntradayTableStatus(b) !== 'waiting'
        )
          return false
        if (
          statusFilter === 'matched' &&
          b.bidStatus !== BiddingIntradayOrderStatus.ALL_MATCHED
        )
          return false
        if (
          statusFilter === 'cancelled' &&
          b.bidStatus !== BiddingIntradayOrderStatus.CANCELLED
        )
          return false
        if (
          statusFilter === 'autoCancelled' &&
          b.bidStatus !== BiddingIntradayOrderStatus.AUTO_CANCELLED
        )
          return false
        // ワークフロー (AC-BIN-018、 複数選択): 空 Set = 「すべて」。 選択済みなら
        // 行のワークフロー (未設定は NO_WORKFLOW sentinel に正規化) がいずれかの
        // 選択 ID と一致する行だけを残す (= 複数ワークフローを横断して確認・取消できる)。
        if (workflowFilter.size > 0) {
          if (!workflowFilter.has(normalizeWorkflowKey(b.workflowId)))
            return false
        }
        // コマフィルタ (AC-BIN-002): 空選択 = すべて (絞り込みなし)。
        if (komaFilter.size > 0 && !komaFilter.has(String(b.timeCode)))
          return false
        return true
      }),
    [areaScopedBids, side, statusFilter, workflowFilter, komaFilter],
  )

  // ----- 提出対象 (AC-BIN-009) -----
  // 提出できる札のうち、 入札一覧に表示中のものだけを提出する。 ボタンの件数・提出確認 modal の
  // 集計・提出 RPC は同じ `submitTargets` を使い、 modal の表示と実際の提出対象を一致させる
  // (spot index.tsx の submitSummary と同型)。 「売買逆転のみ表示」 (AC-BIN-046) で絞った一覧も、
  // 提出対象は表示中の札。
  //
  // 売買逆転の検知対象に入れる下書きは、 「売買逆転のみ表示」 を除いた絞り込み後の一覧の札。
  // その絞り込みは検知結果で決まるので、 検知対象の側には使えない。
  const listedSubmittableBidIds = useMemo(
    () =>
      new Set(filteredBids.filter(isIntradaySubmittableBid).map((b) => b.id)),
    [filteredBids],
  )
  // ----- 売買逆転 (価格交差) 検知 (AC-BIN-045/046) -----
  // 検知対象プールは、 これから提出する札 (一覧に表示中の下書き) に加えて、 板にある / 載る予定の
  // 札 (予約中・承認申請中・承認済・入札中〈一部約定しているものを含む〉) も含める
  // (AC-BIN-045、 issue #8529)。 GC を過ぎたコマの札 (失効・未約定・終わった部分約定) は外す。
  // 板にある / 載る予定の札は表示 filter (area / side / status) に依らない (= 現在の絞り込みに
  // 関わらず、 受渡日全体の交差を知らせる)。 絞り込みで隠れた下書きは提出しないので入れない。
  // 承認確認の明細は下書きを含めない別の検知で、 `useIntradayApprovalDecision` が持つ。
  const inversions = useMemo(
    () =>
      detectIntradayDeliveryDateInversions(allOrders, targetDate, {
        isBeforeGc: (o) => o.timeCode >= gcSlotIndex,
        isSubmitTarget: (o) => listedSubmittableBidIds.has(o.id),
      }),
    [allOrders, targetDate, gcSlotIndex, listedSubmittableBidIds],
  )
  /**
   * [8080 準拠] 売買逆転の札は一覧で強調し、 「売買逆転のみ表示」 でその札だけに絞れる
   * (8080 `bidding/intraday.html` の `.inversion-row` / `#intraday-inv-only-chk`。
   * spot の通常入札一覧と同じ形)。 spec AC-BIN-046 の「前へ / 次へ」 は 8080 で撤去済みで、
   * 書き換えは #9557 で PdM に依頼中。
   *
   * 絞り込みは一覧の行だけに掛ける (= ポジションのグラフは絞らない。 8080 も表の行を隠すだけ)。
   * 逆転が 0 件になったら絞り込みは効かない (8080 は 0 件でチェックを外す)。
   */
  const inversionBidIds = useMemo(
    () => new Set(inversions.flatMap((inv) => inv.bidIds)),
    [inversions],
  )
  const isInversionOnly = inversionOnly && inversions.length > 0

  // ----- 入札中の札の一括板取消 (AC-BIN-112) -----
  // 対象は絞り込み後の一覧 (`filteredBids`。 売買逆転のみ表示 AC-BIN-046 は含めない)。
  const bulkWithdraw = useIntradayBulkWithdraw({
    filteredBids,
    isListLoaded: listData !== undefined,
    isReadonly,
    readonlyReason,
    targetDate,
  })
  // ⋯メニューで強調中の項目 (一括板取消の項目の理由・件数のツールチップを強調中に出す)。
  const [moreMenuHighlighted, setMoreMenuHighlighted] = useState<string | null>(
    null,
  )
  const tableBids = useMemo(
    () =>
      isInversionOnly
        ? filteredBids.filter((b) => inversionBidIds.has(b.id))
        : filteredBids,
    [isInversionOnly, filteredBids, inversionBidIds],
  )

  const submitTargets = useMemo(
    () => tableBids.filter(isIntradaySubmittableBid),
    [tableBids],
  )
  // 受渡日の提出できる札のうち、 絞り込みで一覧に表示していない札の件数 (提出確認 modal の
  // 「対象外」の行)。
  const submittableBidCount = useMemo(
    () => allBids.filter(isIntradaySubmittableBid).length,
    [allBids],
  )
  const excludedByFilterCount = submittableBidCount - submitTargets.length
  const submitDisabledCause = resolveIntradaySubmitDisabledCause({
    isReadonly,
    beforeAcceptance,
    isApprovalFlowUnavailable,
    submitTargetCount: submitTargets.length,
    hasSubmittableBid: submittableBidCount > 0,
  })

  // 提出対象 1 件に送るアクション (AC-BIN-009)。 backend の状態機械に一意に従う:
  // - 承認済 (APPROVED) → SUBMIT (`Submit` は APPROVED から受け付ける。 最終段の承認は
  //   同じ操作で提出まで進む (AC-BIN-095) ので、 承認済の札が残るのは従来の札だけ)
  // - 下書き (DRAFT) かつ承認モード=要承認 → REQUEST (`Submit` は
  //   「SUBMIT from DRAFT requires non-APPROVAL mode」 で拒否する)
  // - 下書き (DRAFT) かつ承認なし / 自動 → SUBMIT
  // 承認申請・直接の JEPX 提出は本ボタン (バッチ) が唯一の導線で、 行単位の詳細
  // モーダルには出さない (issue #4867 の承認粒度。 `IntradayOrderDetailModal` は
  // 承認 / 差戻し / 申請取消 / 再編集 (差戻し後) / 取下のみ)。 承認申請した札の承認は、
  // 一覧の操作列か詳細モーダルから行い、 最終段の承認で同じ操作のまま提出まで進む。
  // 承認モードは対象自身の areaCode を現在の
  // 承認フロー設定から解決する (`resolveIntradayApprovalMode`、 AC-BIN-094 の wizard
  // 表示と同じ解決規則・同じフォールバック)。
  const actionOf = useMemo(
    () =>
      (b: IntradayBidRow): BiddingLifecycleAction =>
        b.approvalState === BiddingApprovalState.DRAFT &&
        resolveIntradayApprovalMode({
          processes: approvalFlowScreenData?.processes,
          isLoading: isApprovalFlowScreenLoading,
          isError: isApprovalFlowScreenError,
          areaCode: b.areaCode,
        }) === BiddingApprovalMode.APPROVAL
          ? BiddingLifecycleAction.REQUEST
          : BiddingLifecycleAction.SUBMIT,
    [
      approvalFlowScreenData,
      isApprovalFlowScreenLoading,
      isApprovalFlowScreenError,
    ],
  )
  // AC-BIN-116: 自動取消時刻を過ぎた札は、直接提出 (SUBMIT) の時だけ事前に外す。
  // 承認申請 (REQUEST) になる札は外さず、下の申請ブロックで提出全体を止める。
  const submitWithdrawAtPassedBids = useMemo(
    () =>
      submitTargets.filter(
        (bid) =>
          actionOf(bid) === BiddingLifecycleAction.SUBMIT &&
          isIntradayWithdrawAtPassed(bid, now),
      ),
    [submitTargets, actionOf, now],
  )
  // AC-BIN-116 承認申請: 要承認の超過札が 1 件でもあれば、直接提出の札も含めて全体を止め、
  // 件数と明細は要承認の超過札だけを出す (#11510 の Des 判断)。
  const requestWithdrawAtBlockedBids = useMemo(
    () =>
      submitTargets.filter(
        (bid) =>
          actionOf(bid) === BiddingLifecycleAction.REQUEST &&
          (isIntradayWithdrawAtPassed(bid, now) ||
            requestWithdrawAtBlockedIds.has(bid.id)),
      ),
    [submitTargets, actionOf, now, requestWithdrawAtBlockedIds],
  )
  const isRequestWithdrawAtBlocked = requestWithdrawAtBlockedBids.length > 0
  // 申請ブロック中は直接提出の超過札も外さない: 除外の節を出さないので、外すとサマリーから
  // 理由なく消え、承認モード混在の確認が承認申請側に切り替わる (design の mixed 表示)。
  const submitEligibleTargets = useMemo(() => {
    if (isRequestWithdrawAtBlocked) return submitTargets
    const passedIds = new Set(submitWithdrawAtPassedBids.map((bid) => bid.id))
    return submitTargets.filter((bid) => !passedIds.has(bid.id))
  }, [submitTargets, submitWithdrawAtPassedBids, isRequestWithdrawAtBlocked])
  const submitSummary = useMemo(
    () => computeIntradayBidKpi(submitEligibleTargets),
    [submitEligibleTargets],
  )
  // 提出の最終確認モーダル (AC-060、 spot AC-077 と同一文言): JEPX 制度上限を満たさない
  // 札が残っていれば違反ブロックを表示し、 提出ボタンを非活性にする。
  const hasJepxViolation = useMemo(
    () => hasIntradayLimitViolation(submitEligibleTargets),
    [submitEligibleTargets],
  )
  // AC-BIN-009: 提出確認モーダルのエリア別内訳 (要承認 / 承認なし / 自動 のいずれでも表示)。
  const submitAreaBreakdown = useMemo(
    () => buildIntradayAreaBreakdown(submitEligibleTargets),
    [submitEligibleTargets],
  )
  // 提出対象のうち承認申請 (REQUEST) になる札。 実際に送るアクションを決める `actionOf`
  // に従属させ、 モーダルの見出し・ボタン文言・事前チェックの対象と実 RPC が乖離しない
  // ようにする。 承認済の札は `actionOf` が SUBMIT を返すので入らない。
  const requestTargets = useMemo(
    () =>
      submitEligibleTargets.filter(
        (b) => actionOf(b) === BiddingLifecycleAction.REQUEST,
      ),
    [submitEligibleTargets, actionOf],
  )
  // AC-BIN-009: 「N件の入札を提出」 の提出対象が全件 承認申請 (REQUEST) になるか
  // (= 承認申請確認モーダルへ出し分けるかの判定)。
  //
  // 提出対象は複数エリアにまたがりうる (`submitTargets` 参照) ため、
  // エリアで承認モードが混在するケースがここでも起こりうる (#6651 未決)。 個々の RPC
  // 呼び出し (`handleSubmitToJepx`) は対象ごとに `actionOf` のアクションを送るので
  // 提出そのものは対象ごとに正しく進む。 モーダルの見出し・ボタン文言は、 全件が
  // 承認申請の時だけ承認申請確認側に切り替え、 1 件でも直接提出 (SUBMIT) が混ざれば
  // T-79 と同じ「これまでの表示」 (直接提出確認側) を維持する。
  const submitRequiresApproval =
    submitEligibleTargets.length > 0 &&
    requestTargets.length === submitEligibleTargets.length
  // 提出確認モーダルの確認項目 (#9586)。 「承認フロー」 は提出対象のエリアの承認フロー設定の
  // 値 (設定を読めていない間は出さない)、 「JEPX への送信」 は実際に送るアクション
  // (`actionOf`) から決める。 札ごとに承認申請か提出かが決まるので、 両方が混ざる時は
  // それぞれのタイミングを書く (AC-BIN-009)。
  const submitApprovalModes = useMemo(
    () =>
      isApprovalFlowUnavailable
        ? undefined
        : groupAreasByApprovalMode(
            submitEligibleTargets.map((b) => b.areaCode),
            resolveApprovalModeForArea,
          ),
    [
      isApprovalFlowUnavailable,
      submitEligibleTargets,
      resolveApprovalModeForArea,
    ],
  )
  const submitSendTiming: SubmitSendTiming = submitRequiresApproval
    ? 'afterApproval'
    : requestTargets.length > 0
      ? 'mixed'
      : 'onSubmit'
  // AC-BIN-015 / AC-BDL-014: テナント設定の入札制限 (= 入札制限画面の 時間前 matrix、
  // スポットとは独立) に抵触する札の扱い。 判定値は保存済み設定そのもの (= BFF
  // GetBidLimitMatrix の INTRADAY セル) なので、 入札制限画面で保存した内容がそのまま
  // 本画面のチェックに効く (AC-BDL-014)。
  // JEPX 制度上限 (AC-BIN-038) とは独立した違反種別として別枠に出す (AC-BIN-039)。
  const {
    limits: intradayBidLimits,
    isLoading: isBidLimitsLoading,
    isError: isBidLimitsError,
  } = useBidLimits(BidSegment.INTRADAY)
  // 提出前の事前チェック (= 保存済み設定との突合) の節。 対象は承認申請になる札
  // (`requestTargets`) だけで、 違反があれば確定を止める (AC-BIN-015 の ※)。 1 札の
  // 価格・数量は札ごとに、 上限数量(コマ計) は承認申請になる札を含む (エリア × コマ × 売買) の
  // 合計 (既に出ている札 `allBids` + 今回申請する札) で判定する。 直接提出 (SUBMIT) の札は
  // 止めず、 違反した札だけを backend が拒否して提出の対象から外す (#8317)。 承認モードが
  // 混ざる提出で要承認の札に違反があれば、 直接提出の札も含めて全体が止まる (混ざる時の
  // 確認モーダルの出し方は AC-BIN-009 がデザイナー判断としている)。 承認済の札は承認申請の
  // 対象に入らない (承認時 AC-BIN-097 に検証済み) が、 コマ計の合計には既に出ている札として数える。
  const preCheckLimitSection = useMemo(
    () =>
      buildIntradayLimitViolationSection(
        { requestBids: requestTargets, existingBids: allBids },
        intradayBidLimits,
        t,
      ),
    [requestTargets, allBids, intradayBidLimits, t],
  )
  // AC-BIN-015: 直近の提出試行で backend が入札制限違反で拒否した札の明細
  // (bidLimitViolations、 handleSubmitToJepx が RPC 失敗から詰める) の節。 提出した件数
  // (#8317 の sentCount) もここで伝える。
  const bidLimitViolationSection = useMemo(
    () =>
      buildIntradayBidLimitViolationSection(
        bidLimitViolations,
        bidLimitSentCount,
        t,
        'submit',
        bidLimitReservedOpenTime,
      ),
    [bidLimitViolations, bidLimitSentCount, bidLimitReservedOpenTime, t],
  )
  const submitViolationSections = useMemo(
    () => [
      !submitResultOpen && isRequestWithdrawAtBlocked
        ? buildIntradayRequestWithdrawAtBlockedSection(
            requestWithdrawAtBlockedBids,
            t,
          )
        : buildIntradayWithdrawAtPassedSection(
            submitResultOpen
              ? withdrawAtPassedBids
              : submitWithdrawAtPassedBids,
            t,
            submitResultOpen ? 'result' : 'beforeSubmit',
          ),
      // 同じ違反を事前チェックと RPC 由来で二重に出さない: backend が実際に弾いた明細が
      // あればそれを優先 (件数・提出した件数の文言が事実と一致する)、 無ければ事前チェック。
      bidLimitViolations.length > 0
        ? bidLimitViolationSection
        : preCheckLimitSection,
      // JEPX 制度上限違反 (AC-BIN-038) をテナント設定上限違反とは独立した節として
      // 必ず積む。 `SubmitConfirmModal` は details が空の節を描画しないので、
      // テナント設定違反が同時に立っている時だけ制度上限違反が確認モーダルから
      // 消えていた不具合 (AC-BIN-039「区別して表示する」未達。レビュー是正
      // 2026-09-19) を、 節を常時渡す形に直して解消する。
      buildIntradayJepxViolationSection(submitEligibleTargets, t),
      // 取得できていない間 (初回応答前の isLoading も含む) は事前チェックが「制限未設定 =
      // 違反なし」に見えるだけで検証が成立していない (= 空 lookup) ため fail-closed に
      // する (codex 指摘 2026-09-18: isLoading を見ないと応答前に申請できてしまう)。
      // 止めるのは事前チェックの対象 (承認申請になる札) がある時だけ。 直接提出だけの
      // 提出は画面が入札制限を使わず、 backend が検証する (ensureWithinIntradayBidLimits)。
      ...(requestTargets.length > 0 && (isBidLimitsLoading || isBidLimitsError)
        ? [buildIntradayBidLimitsUnavailableSection(t)]
        : []),
    ],
    [
      bidLimitViolations.length,
      bidLimitViolationSection,
      preCheckLimitSection,
      submitEligibleTargets,
      submitResultOpen,
      isRequestWithdrawAtBlocked,
      requestWithdrawAtBlockedBids,
      submitWithdrawAtPassedBids,
      withdrawAtPassedBids,
      requestTargets.length,
      isBidLimitsLoading,
      isBidLimitsError,
      t,
    ],
  )
  // 明細を持つ節が 1 つでもあれば提出不可 (テナント設定上限違反・JEPX 制度上限違反の
  // いずれも該当)。
  // 先頭の自動取消時刻超過は直接提出なら「外して残りを出す」注意であり、混在時の
  // 確定を止めない。残りの section は従来どおり提出不可の違反/取得不能を表す。
  const hasBidLimitViolation = submitViolationSections
    .slice(1)
    .some((s) => s.details.length > 0)
  // AC-BIN-009: フォーミュラ価格の札は、 保存時点の評価値 (= 保存した入札価格。 AC-BIN-020 で
  // 固定した値で、 送信時に計算し直さない) を併記する。
  const submitFormulaDetailLines = useMemo(
    () =>
      submitEligibleTargets.flatMap((b) => {
        const formula = ordersById.get(b.id)?.formula
        if (formula === undefined) return []
        return [
          t('page:biddingIntraday.submit.formulaDetail', {
            koma: b.timeCode,
            area: convertAreaLabel(b.areaCode),
            side: t(
              b.direction === Side.BUY
                ? 'page:biddingSpot.side.buy'
                : 'page:biddingSpot.side.sell',
            ),
            formula: formulaSummary(t, formula),
            price: formatYenPerKwh(b.priceYenPerKwh),
          }),
        ]
      }),
    [submitEligibleTargets, ordersById, t],
  )
  const submitWarningSections = useMemo(
    () => [
      buildSuspendedKomaWarningSection(
        submitEligibleTargets,
        suspendedTimeCodes,
        t,
      ),
    ],
    [submitEligibleTargets, suspendedTimeCodes, t],
  )
  const positions = useMemo(
    () => buildIntradayPositionSlots(filteredBids, needByKoma),
    [filteredBids, needByKoma],
  )
  const coverage = useMemo(
    () => summarizeIntradayCoverage(positions),
    [positions],
  )
  // AC-BIN-132: クリアは一覧固有のフィルタ (AC-BIN-018) と 「売買逆転のみ表示」
  // (AC-BIN-046) だけを戻す。 全体フィルター (エリア・売買区分) は戻さない。
  const resetFilters = (): void => {
    updateListFilters(defaultIntradayBidListFilters)
  }
  // 開閉式フィルターの件数 (既定から変えている一覧固有の条件。売買区分は全体フィルターなので数えない)
  const modifiedFilterCount =
    (statusFilter !== '' ? 1 : 0) +
    (workflowFilter.size > 0 ? 1 : 0) +
    (komaFilter.size > 0 ? 1 : 0) +
    (inversionOnly ? 1 : 0)
  const statusFilterOptions = [
    { value: '', label: t('common:filter.all') },
    {
      value: 'placed',
      label: t('page:biddingIntraday.tableStatus.waiting'),
    },
    {
      value: 'matched',
      label: t('page:biddingIntraday.tableStatus.matched'),
    },
    // 自動取消は取消の前 (AC-BIN-035、 main の 1b462710ec と同じ並び)
    {
      value: 'autoCancelled',
      label: t('page:biddingIntraday.tableStatus.autoCancelled'),
    },
    {
      value: 'cancelled',
      label: t('page:biddingIntraday.tableStatus.cancelled'),
    },
  ]
  // 要約は既定でも「すべて」と出す (空白にしない)
  const multiSummary = (count: number): string =>
    count === 0
      ? t('common:filter.all')
      : t('common:filter.selectedCount', { count })
  const filterSummary: ToolbarStatusItem[] = [
    {
      key: 'status',
      label: t('page:biddingIntraday.column.status'),
      value:
        statusFilterOptions.find((o) => o.value === statusFilter)?.label ??
        t('common:filter.all'),
    },
    {
      key: 'workflow',
      label: t('page:biddingIntraday.bidCreate.workflowLabel'),
      value: multiSummary(workflowFilter.size),
    },
    {
      key: 'koma',
      label: t('page:biddingIntraday.komaFilter.label'),
      value: multiSummary(komaFilter.size),
    },
    ...(inversionOnly
      ? [
          {
            key: 'inversionOnly',
            label: t('page:biddingIntraday.alert.inversionFilter', {
              count: inversions.length,
            }),
            value: '',
          },
        ]
      : []),
  ]
  const filterGridItems: FilterGridItem[] = [
    {
      key: 'status',
      label: t('page:biddingIntraday.column.status'),
      control: (
        <DsSelect
          width={STATUS_FILTER_WIDTH}
          aria-label={t('page:biddingIntraday.column.status')}
          onChange={(value) =>
            updateListFilters((prev) => ({
              ...prev,
              status: STATUS_FILTER_VALUES.find((v) => v === value) ?? '',
            }))
          }
          options={statusFilterOptions}
          value={statusFilter}
        />
      ),
    },
    {
      key: 'workflow',
      label: t('page:biddingIntraday.bidCreate.workflowLabel'),
      // AC-BIN-018 (いずれも複数選択): ワークフロー単位の絞り込み。 選択肢は一覧に実在する
      // workflowId を動的に列挙する (workflowFilterOptions)。 ワークフローを経由せずに作った札は
      // 「ワークフローなし」 で絞り込める
      control: (
        <FilterMultiSelect
          minWidth="140px"
          onChange={(next) =>
            updateListFilters((prev) => ({ ...prev, workflows: next }))
          }
          options={workflowFilterOptions}
          selected={workflowFilter}
        />
      ),
    },
    {
      key: 'koma',
      label: t('page:biddingIntraday.komaFilter.label'),
      // コマフィルタ (AC-BIN-002)。 選択肢ラベル末尾にコマ状態 (AC-BIN-054) を後置する (#7603 Des 判断案 B)
      control: (
        <Box width="200px">
          <MultiSelect
            aria-label={t('page:biddingIntraday.komaFilter.ariaLabel')}
            onChange={(next) =>
              updateListFilters((prev) => ({ ...prev, komas: new Set(next) }))
            }
            options={komaFilterOptions}
            placeholder={t('page:biddingIntraday.komaFilter.placeholder')}
            selected={Array.from(komaFilter)}
          />
        </Box>
      ),
    },
    {
      key: 'inversionOnly',
      // AC-BIN-046。 Alert に入力部品を置かないため絞り込みのカードに置く。
      // 検知 0 件でも出し、 チェックは残す (0 件の間は絞らない)。
      control: (
        <Checkbox
          checked={inversionOnly}
          onCheckedChange={(details: { checked: boolean | 'indeterminate' }) =>
            updateListFilters((prev) => ({
              ...prev,
              inversionOnly: details.checked === true,
            }))
          }
        >
          {t('page:biddingIntraday.alert.inversionFilter', {
            count: inversions.length,
          })}
        </Checkbox>
      ),
    },
  ]

  const areaLabel = buildSelectedAreasLabel(selectedAreas)
  // 単一エリア選択時のみ、 そのエリアを札作成 modal の prefill に使う。
  const singleSelectedArea =
    selectedAreas.size === 1 ? [...selectedAreas][0] : undefined
  // AC-BIN-012b: ポジション chart は単一エリア選択時のみ描画する。 エリアフィルタの
  // 「すべて」 (= 全 9 エリア集合) や複数エリア選択では need / cover がエリアをまたいで合算され、
  // 需給の異なるエリアを足した数値になって業務上意味を持たない (spot AC-BSP-016a と同趣旨)。
  const isSingleAreaSelected = singleSelectedArea !== undefined

  // chart を描画できない間は accordion 自体も畳む (= 高さを取るガイドを常時開いたままにしない)。
  // エリアフィルタの切替に追従させ、 単一エリアに戻したら開き直す。 render 中の派生 state 更新に
  // しているのは、 useEffect だと「すべて」 に切替えた 1 フレームだけ開いたまま描画されるため。
  const [positionOpen, setPositionOpen] = useState<string[]>(
    isSingleAreaSelected ? ['position'] : [],
  )
  const [prevSingleAreaSelected, setPrevSingleAreaSelected] =
    useState(isSingleAreaSelected)
  if (prevSingleAreaSelected !== isSingleAreaSelected) {
    setPrevSingleAreaSelected(isSingleAreaSelected)
    setPositionOpen(isSingleAreaSelected ? ['position'] : [])
  }

  /**
   * AC-BIN-012c [P0]: 単一エリア以外 (複数エリア選択・0件選択のいずれも) では、
   * accordion trigger の充足率バッジと数量を「—」にする。
   *
   * chart 本体は AC-BIN-012b で既に畳んでいるが、 **trigger は畳んでも出続ける**ので
   * バッジ側を個別に落とさないとエリア横断の合算値が出たままになる。 need / cover を
   * エリアをまたいで足した値は業務上意味を持たない (需給の向きが逆のエリア同士で
   * 相殺される) ため、 数値を見せないのが spec の判断 (issue #4767)。
   */
  const shownCoverageRate = isSingleAreaSelected
    ? coverage.coverageRate
    : undefined
  // 色も同時に落とす。 数値を「—」にしながら緑・橙の状態色だけ残ると、
  // 「—」 なのに良し悪しが付いているように読めるため。
  const { badgeColor, accordionBg } = coverageBadgeColorsOf(
    isSingleAreaSelected ? coverage.state : 'neutral',
  )

  // 札作成の導線 (入札追加 split button の主ボタン) が開いているか。 主ボタンの disabled 条件と同じ。
  const canCreateBid =
    memberBidDisabledReason === undefined &&
    !isReadonly &&
    !beforeAcceptance &&
    firstOpenKoma !== undefined &&
    !noIssuer &&
    !isApprovalFlowUnavailable

  // ----- コマクリック → 札作成 modal を prefill 付きで開く -----
  const openCreateForKoma = (koma: number): void => {
    // workflow-back-bar-readonly AC-16: ワークフローケース完了済み/削除済みは
    // 注文作成不可 (コマクリック経由の作成も含む)。
    if (isReadonly || memberBidDisabledReason !== undefined) return
    // AC-BIN-089c: 受付前 (受渡日前日 12:00 JST より前) は札を作れない。
    // リボン側も 'pre-acceptance' で非活性だが、 chart のコマクリック経由も
    // 同じ導線なのでここで塞ぐ。
    if (beforeAcceptance) return
    // AC-BIN-056: 発注主体が 1件も登録されていないと札は作れない。 入札追加▽ の
    // 3 導線は disabled で塞いでいるが、 ポジション chart の
    // コマクリックも札作成の主動線であり本関数が唯一の入口なので、 同じガードを
    // ここにも置く (= どの経路からも札作成モーダルを開かない)。
    if (noIssuer) return
    // 2026-09-16 決定: 「入札追加」ボタン・「一括作成」メニュー項目は
    // `isApprovalFlowUnavailable` で非活性にしているが、 コマクリック経由の個別作成
    // モーダルはこのガードが漏れていたため、 他 2 導線と揃えて塞ぐ。
    if (isApprovalFlowUnavailable) return
    // 実在しないコマ (= 過去日の境界値 49 等) は無視する。
    if (koma < 1 || koma > 48) return
    // GC 済コマ (= gcSlotIndex より前) は入札不可 (= design `onClick` で slot < gcSlot を skip)
    if (koma < gcSlotIndex) return
    // AC-BIN-002a: 単一エリア以外 (複数エリア選択・0件選択) では 売/買・入札量を
    // prefill せず空欄で開く。 need / cover のエリア横断合算から出した方向・数量は
    // 業務上意味を持たず、 誤った向き・数量の入札を招く (issue #4767。 chart を
    // 非表示にする AC-BIN-012b と一貫させる)。 エリアもモーダル内で選ばせる。
    //
    // `side: ''` を**明示的に**渡す。 modal は `{ ...EMPTY_DEFAULTS, ...prefill }` で
    // 初期値を作り `EMPTY_DEFAULTS.side` は `'1'` (買) なので、 単に省くと「買」 が
    // 選択済みで開いてしまい AC を満たさない (codex 指摘 2026-08-25)。 空にすれば
    // zod の `side: z.string().min(1, '売/買を選択してください')` が明示選択を促す。
    if (!isSingleAreaSelected) {
      setCreatePrefill({ timeCode: String(koma), side: '' })
      setCreateOpen(true)
      return
    }
    const pos = positions[koma - 1]
    const need = pos?.needMw ?? 0
    const cover = pos?.coverMw ?? 0
    const diffMw = Math.abs(need - cover)
    // フォームの数量欄・保持とも MW。 書込粒度 0.1 MW のグリッドへ丸めてから
    // prefill する (= spot NormalBidTab の remain prefill と同規則)。
    const prefillMw = Math.round(diffMw * 10) / 10
    // AC-BIN-002a: 丸め後の数量が 0 (need == cover、 または差分が 0.1MW 未満で
    // 実質過不足なし) のときは**売/買も**空欄で開く。 過不足が無いのに向きだけ
    // 決まっていると、 数量を手で入れた瞬間に根拠の無い向きで発注できてしまう。
    // 不足 (need > cover) なら買い、 余剰 (cover > need) なら売りを初期選択。
    const side = prefillMw > 0 ? (cover > need ? '2' : '1') : ''
    setCreatePrefill({
      areaCode: String(singleSelectedArea),
      timeCode: String(koma),
      side,
      volumeMw: prefillMw > 0 ? prefillMw.toFixed(1) : '',
    })
    setCreateOpen(true)
  }

  // ----- 入札追加 submit (= CreateBiddingIntradayOrder で RPC 永続化) -----
  // 承認モードは Create 対象エリアの承認フロー設定から解決する (AC-BIN-094 / #6650、
  // `useIntradayBidCreateSubmit` 内部で `resolveIntradayApprovalMode` を使う。
  // 個別作成モーダルは常に単一エリアが確定しているので混在は起きない)。
  // proto (CreateBiddingIntradayOrderRequest) に未定義の design 項目は送っていない
  // (= proto 拡張が必要): 入札方式 (bidMode) / タグ。
  // 自動取消時刻 (withdrawAt) は #5007 で proto 対応済み (hook 内で withdrawAtLocal として配線)。
  // 確定処理は板発注のクイック入札と共有する (GC 再判定などのガードを
  // 2 画面に複製しないため。 詳細は useIntradayBidCreateSubmit の JSDoc)。
  const submitCreate = useIntradayBidCreateSubmit({
    targetDate,
    targetDateFilter,
  })
  // AC-BIN-004b: 作成・編集モーダルは開いた時に手数料自動考慮の状態を読む (応答を待たない) ので、
  // 開く前から取得しておく。
  const feeState = useIntradayFeeState()
  const handleCreateSubmit = async (
    result: IntradayBidCreateResult,
  ): Promise<void> => {
    if (memberBidDisabledReason !== undefined) return
    if (await submitCreate(result)) {
      setCreateOpen(false)
    }
  }

  // 一括作成の重複の警告 (AC-BIN-006b) の比べる相手 (選び方は model)。
  const bulkExistingBids = useMemo(
    () => intradayBulkComparableBids(allOrders, targetDate),
    [allOrders, targetDate],
  )

  // ----- 一括作成 submit (AC-BIN-006b): 対象コマぶん Create RPC をループする -----
  // 個別作成と同じく、 承認モードは対象エリア (= フォームで選んだ単一エリア、 生成札
  // 全件に同一適用) の承認フロー設定から解決する (AC-BIN-094 / #6650)。 数量の MWh
  // 換算も同じ write seam (buildCreateIntradayOrderRequest) に委ねる。
  const handleBulkCreateSubmit = async (
    params: IntradayBulkCreateConfirmedParams,
    timeCodes: number[],
    previewGcExcluded: number,
  ): Promise<IntradayBulkCreateOutcome> => {
    if (memberBidDisabledReason !== undefined) return 'settled'
    // 2026-09-16 決定: 一括作成の導線 (メニュー項目) は `isApprovalFlowUnavailable` で
    // 既に非活性だが、 modal を開いたまま設定取得が isLoading→isError へ変わるレースを
    // 塞ぐため確定処理側でも弾く (= 自動フォールバックで作成する経路を残さない)。
    if (isApprovalFlowUnavailable) {
      showErrorToast({
        title: t('page:biddingIntraday.bidCreate.approvalFlowUnavailable'),
      })
      return 'settled'
    }
    // プレビューを開いたままコマ / 日付をまたぐと GC 通過済コマが対象に残り得る。
    // backend (Create) も gc_at を検証してリクエストを拒むが (#5011)、 それだけに
    // 任せず確定時点の GC 境界でクライアント側でも弾き直し、 早期にエラー表示する
    // (境界は受渡日依存なので `gcBoundarySlot` を使う)。
    const confirmBoundary = gcBoundarySlot(serverNow(), targetDate)
    const targets = timeCodes.filter((timeCode) => timeCode >= confirmBoundary)
    // GC 済で外したコマ = プレビューで外したコマ + 確定の時点で GC を過ぎたコマ。
    const gcSkipped = previewGcExcluded + (timeCodes.length - targets.length)
    if (targets.length === 0) {
      showErrorToast({
        title: t('page:biddingIntraday.bidCreate.gcExceeded'),
      })
      return 'settled'
    }
    // 承認モードは対象エリアの承認フロー設定から解決し、 生成する全札に同一適用する
    // (AC-BIN-094 / #6650。 一括作成は単一エリア指定のフォームなので混在は起きない)。
    const bulkApprovalMode = resolveIntradayApprovalMode({
      processes: approvalFlowScreenData?.processes,
      isLoading: isApprovalFlowScreenLoading,
      isError: isApprovalFlowScreenError,
      areaCode: params.areaCode,
    })
    const results = await Promise.allSettled(
      targets.map((timeCode) =>
        bulkCreateAsync(
          buildCreateIntradayOrderRequest({
            targetDate: targetDateFilter,
            areaCode: params.areaCode,
            timeCode,
            direction: params.direction,
            // フォーミュラは入力値を全札に載せ、 backend が札ごとに保存時に計算する (AC-BIN-020)。
            price: params.price,
            volumeMw: params.volumeMw,
            // 発注主体は一括作成モーダルで選んだものを全生成札へ適用する。
            issuerType: params.issuerType,
            gcAt: timestampFromDate(calcGcAtForKoma(targetDate, timeCode)),
            approvalMode: bulkApprovalMode,
          }),
        ),
      ),
    )
    const failed = results.filter((r) => r.status === 'rejected').length
    if (failed < targets.length) {
      saveLastBidAreas('intraday', [params.areaCode])
    }
    // 手数料自動考慮の設定・単価・判定がプレビュー時から変わった拒否 (#9213) と、 フォーミュラの
    // 変数の値・計算結果での拒否 (AC-BIN-020) は、 理由別の文言で知らせる。 プレビューの
    // 取り直しは作成 mutation が行い、 全件が手数料の理由で失敗したらモーダルを Step1 に戻す。
    const rejections = results.flatMap((r): unknown[] =>
      r.status === 'rejected' ? [r.reason as unknown] : [],
    )
    const staleError = rejections.find(isFeeAdjustedPricePreviewStale)
    const reasonError =
      staleError ?? rejections.find(isFormulaPricePreviewStale)
    const reasonMessage =
      reasonError !== undefined
        ? translateIntradayBidCreateError(reasonError, t)
        : undefined
    // バナーには、 失敗した札の理由を重複なく全部並べる (手数料・フォーミュラの理由も、 GC 超過などの
    // 既知の理由も)。 理由を特定できない拒否が 1 件でもあれば「時間をおいて再度お試しください」 を添える
    // (AC-BIN-006b)。
    const rejectionReasons = rejections.map((err) =>
      intradayBidCreateErrorReason(err, t),
    )
    const bannerReasons = [
      ...new Set(
        rejectionReasons.filter(
          (reason): reason is string => reason !== undefined,
        ),
      ),
    ]
    const hasUnknownReason = rejectionReasons.some(
      (reason) => reason === undefined,
    )
    // 前回の結果のバナーは、 新しい結果が確定した時点で必ず置き換える (全件成功・全件失敗でも残さない)。
    setBulkResult(undefined)
    if (failed === 0 && gcSkipped === 0) {
      showSuccessToast({
        title: t('page:biddingIntraday.bulkCreate.success', {
          count: targets.length,
        }),
      })
      setBulkOpen(false)
      return 'settled'
    }
    if (failed === targets.length) {
      // 全件が同じ既知の理由 (受渡契約コードを決められない等、 一括作成の全札が共有する入力に
      // 起因する拒否) で失敗した時は、 その理由別の文言を出す (AC-BIN-006b)。 理由が混ざる・
      // 分からない失敗が 1 件でもあれば汎用文言のまま。
      // reason の無い FailedPrecondition は一括作成バナー側では GC 超過に丸めるが、 全件失敗の
      // トーストでは丸めない (上限数量超過等の実際の原因と食い違うため)。 reason を持つ失敗だけで
      // 1 種類に揃う時だけ専用文言にする。
      const explicitReasons = rejections.map((err) =>
        intradayBidCreateExplicitReason(err, t),
      )
      const explicitReasonSet = new Set(explicitReasons)
      const knownReasonMessage =
        explicitReasonSet.size === 1 && !explicitReasonSet.has(undefined)
          ? explicitReasons[0]
          : undefined
      showErrorToast({
        title:
          reasonMessage ??
          knownReasonMessage ??
          t('page:biddingIntraday.bulkCreate.error'),
      })
      return staleError !== undefined ? 'feePreviewStale' : 'settled'
    }
    // 一部の札だけ作れた (GC 済で外したコマがある、 または作成に失敗した札がある) 時は、
    // トーストに代えて入札追加タブの上部のエラーバナーで知らせる (ui-message-guide
    // 「まとめて行った操作の一部だけが失敗した」)。
    setBulkResult({
      targetDate,
      created: targets.length - failed,
      gcExcluded: gcSkipped,
      failed,
      reasons: bannerReasons,
      hasUnknownReason,
    })
    setBulkOpen(false)
    return 'settled'
  }

  // AC-BIN-095: 一覧の操作列「承認する」「差戻し」 → IntradayOrderDetailModal と同じ
  // 遷移 (APPROVE/REJECT) を呼ぶ。 IntradayBidRow は RPC 生値 (gcAt/updatedAt 等) を
  // 持たないため、 行クリック (AC-007) と同じく ordersById から IntradayOrderRow を引く。
  // 読み取り専用 (= 完了済 / 削除済のワークフローケース経由) では、 行クリックの詳細
  // (handleRowClick) と同じく操作口を塞ぐ。 表示側の disabled だけに頼ると、 DS の
  // 無効化表現 (aria-disabled) が変わった時に承認が通ってしまう。
  // AC-BIN-097: 承認確認モーダルの売買逆転の警告は、 受渡日の全入札 (allOrders) から hook が
  // 検知する (承認はブロックしない)。 確定直前に GC を跨いでいて止めた理由はトーストに出す
  // (確認 UI は閉じるので、 一覧の上に出す)。
  const approvalDecision = useIntradayApprovalDecision({
    orders: allOrders,
    reportGateCheck: (blockedReason) => {
      if (blockedReason !== undefined) showErrorToast({ title: blockedReason })
    },
  })
  // AC-BIN-098: 承認者非該当 / 自己承認の注意表示を操作口の近傍に常時出す (押下は止めない)。
  // 押したときの確認モーダルの警告は approvalDecision が持つ。
  const approverContext = useIntradayApproverContext()
  const approvalNoticeOf = (row: IntradayBidRow): string | undefined => {
    const notices = approverContext.persistentNotices(row)
    return notices.length > 0 ? notices.join(' ') : undefined
  }
  const handleApprove = (row: IntradayBidRow): void => {
    if (isReadonly) return
    const order = ordersById.get(row.id)
    if (order !== undefined) approvalDecision.openApprove(order)
  }
  const handleReject = (row: IntradayBidRow): void => {
    if (isReadonly) return
    const order = ordersById.get(row.id)
    if (order !== undefined) approvalDecision.openReject(order)
  }

  // AC-007: 一覧行クリック・行の編集ボタン (#10110) → 詳細/編集モーダルを開く
  // (中間表現の IntradayOrderRow を渡す)。
  const handleRowClick = (row: IntradayBidRow): void => {
    if (isReadonly) return
    const order = ordersById.get(row.id)
    if (order !== undefined) setDetailOrder(order)
  }

  // ----- 提出 (= 提出対象を 1 件ずつ SUBMIT/REQUEST して進める、 AC-BIN-009) -----
  // 提出対象と対象ごとのアクションは `submitTargets` / `actionOf` (上のコメント参照)。
  // エリアで承認モードが混在する提出 (#6651 未決) でも、 対象ごとに正しいアクションを
  // 選べば個々の RPC は失敗しない (モーダルの見出し・文言は `submitRequiresApproval` —
  // 全件が承認申請の時だけ承認申請側に出し分ける)。
  const handleSubmitToJepx = async (): Promise<void> => {
    // 2026-09-16 決定: 提出ボタンは `isApprovalFlowUnavailable` で既に非活性だが、
    // モーダルを開いたまま設定取得の状態が変わるレースを塞ぐため確定処理側でも弾く。
    // `actionOf` はフォールバック (AUTO) の間 SUBMIT を選ぶため、 対象の本当の承認モードが
    // 要承認なら backend の SUBMIT ガード (DRAFT かつ承認モード=要承認は SUBMIT 拒否) に
    // 抵触する — 誤ったアクションを推測で送らせない。
    if (isApprovalFlowUnavailable) {
      showErrorToast({
        title: t('page:biddingIntraday.bidCreate.approvalFlowUnavailable'),
      })
      setSubmitModalOpen(false)
      return
    }
    // 確認を開いた後に同じ分へ到達しても、送信直前の server-offset 時刻で再分類する。
    const submissionNow = serverNow()
    // AC-BIN-116: 承認申請になる札が時刻を過ぎたら 1 件も送らず、申請ブロックに切り替える。
    const justPassedRequests = submitEligibleTargets.filter(
      (bid) =>
        actionOf(bid) === BiddingLifecycleAction.REQUEST &&
        isIntradayWithdrawAtPassed(bid, submissionNow),
    )
    if (justPassedRequests.length > 0) {
      setRequestWithdrawAtBlockedIds(
        new Set(justPassedRequests.map((bid) => bid.id)),
      )
      return
    }
    const justPassed = submitEligibleTargets.filter(
      (bid) =>
        actionOf(bid) === BiddingLifecycleAction.SUBMIT &&
        isIntradayWithdrawAtPassed(bid, submissionNow),
    )
    const preExcluded = [
      ...new Map(
        [...submitWithdrawAtPassedBids, ...justPassed].map((bid) => [
          bid.id,
          bid,
        ]),
      ).values(),
    ]
    const preExcludedIds = new Set(preExcluded.map((bid) => bid.id))
    const targets = submitEligibleTargets.filter(
      (bid) => !preExcludedIds.has(bid.id),
    )
    if (targets.length === 0) {
      // info type は success/error 専用の useSuccessToast/useErrorToast の対象外の
      // ため appToaster を直接参照する。
      appToaster.create({
        type: 'info',
        title: t('page:biddingIntraday.submit.noTarget'),
      })
      if (preExcluded.length > 0) {
        setWithdrawAtPassedBids(preExcluded)
        setSubmitResultOpen(true)
      } else {
        setSubmitModalOpen(false)
      }
      return
    }
    const attempts = targets.map((b) => ({ bid: b, action: actionOf(b) }))
    const results = await Promise.allSettled(
      attempts.map(({ bid, action }) =>
        submitAsync({
          id: bid.id,
          action,
          expectedUpdatedAt: bid.updatedAt,
        }),
      ),
    )
    // AC-BIN-015 / AC-BIN-057: 札ごとの結果 (提出済 / 承認申請済 / 入札制限違反で拒否 /
    // その他の失敗) の件数と違反の明細。 1 札・価格の違反の明細は backend
    // ensureWithinIntradayBidLimits が ErrorInfo.metadata に積む設定側の値 + この札自体の
    // 入力値から、 上限数量(コマ計) の違反は backend が数えた合計と上限の metadata から
    // 組み立てる。
    // 通らなかった札のうち、 結果の時点でそのコマの GC を過ぎていた札は GC 超過の理由で出す (AC-BIN-009)。
    const resultBoundary = gcBoundarySlot(serverNow(), targetDate)
    const summary = summarizeIntradaySubmitResults(
      attempts.map((a, i) => ({ ...a, result: results[i] })),
      (bid) => bid.timeCode < resultBoundary,
    )
    setSubmitFailure(
      summary.toast?.kind === 'partialFailure' ||
        (summary.toast?.kind === 'allFailed' && !summary.toast.retryLaterOnly)
        ? {
            targetDate,
            passedCount:
              summary.toast.kind === 'partialFailure' ? summary.toast.ok : 0,
            failures: summary.failures,
          }
        : undefined,
    )
    // API 再取得後に行が消えても、提出時点で外した札の明細を表示し続ける。
    // backend の実行時判定は画面上の事前判定より新しいので、同じ id は backend 側を優先する。
    const withdrawAtPassed = [
      ...new Map(
        [...preExcluded, ...summary.withdrawAtPassed].map((bid) => [
          bid.id,
          bid,
        ]),
      ).values(),
    ]
    setWithdrawAtPassedBids(withdrawAtPassed)
    setSubmitResultOpen(withdrawAtPassed.length > 0)
    setBidLimitViolations(summary.violations)
    // #8317: 違反明細の本文「違反した N件を除き、 M件を提出しました」 の M。 AC-BIN-009 に
    // 従い、 バナーの {S} と同じ通った件数 (承認申請・予約を含む) で数える。
    setBidLimitSentCount(summary.submittedCount + summary.requestedCount)
    // AC-10: 完了通知の文言は提出 RPC の response が返す send_status = RESERVED
    // を SoT にする (frontend の marketOpen.ts 再判定では駆動しない — local
    // timezone 依存で backend の JST 判定とズレうるため、 §6 Layer 間接続点)。
    // 同一受渡日の一括提出は全件が同じ開場前/後の側に落ちるため、 いずれか 1 件の
    // response を代表値として見れば足りる。 REQUEST は送信軸を持たないため常に false。
    const reserved = results.some(
      (r) =>
        r.status === 'fulfilled' &&
        r.value.order?.sendStatus === BiddingBidSendStatus.RESERVED,
    )
    setBidLimitReservedOpenTime(reserved ? marketOpenTime : undefined)
    // エリアで承認モードが混在した提出は、 承認申請 (REQUEST = 板未到達) と
    // JEPX 提出 (SUBMIT) の件数を分けて通知する (#8321 の EN 暫定判断 2026-09-19。
    // PdM の事後確認は #8404)。 従来は全件が承認申請の時だけ文言が切り替わるため、
    // 混在時は承認申請しただけの札まで「JEPX に提出しました ({n} 件)」に数えていた。
    if (summary.toast?.kind === 'success') {
      showSuccessToast({
        // successReserved の文言は AC-BIN-057 の固定文言 (#4917 で spec 確定済み)。
        // requestSuccess (承認申請) / mixedSuccess (混在) の文言は本 PR (#8074) で
        // 追加。 REQUEST は送信軸を持たないため、 混在時の reserved は SUBMIT 分に
        // だけかかる (予約になる旨は提出確認モーダルの注記で既に示している)。
        title: submitRequiresApproval
          ? t('page:biddingIntraday.submit.requestSuccess', {
              count: targets.length,
            })
          : summary.requestedCount > 0
            ? t('page:biddingIntraday.submit.mixedSuccess', {
                requestCount: summary.requestedCount,
                submitCount: summary.submittedCount,
              })
            : reserved
              ? t('page:biddingIntraday.submit.successReserved', {
                  count: targets.length,
                  openTime: marketOpenTime,
                })
              : t('page:biddingIntraday.submit.success', {
                  count: targets.length,
                }),
      })
    } else if (
      summary.toast?.kind === 'allFailed' &&
      summary.toast.retryLaterOnly
    ) {
      // 1 件も通らず、 通らなかった札がすべて時間をおけば通る失敗 (通信の不調等) の時だけトースト
      // (ui-message-guide「部品の選び方」)。 それ以外は上の submitFailure のエラーバナーで知らせる。
      showErrorToast({ title: t('page:biddingIntraday.serverError.unknown') })
    } else if (summary.toast?.kind === 'requested') {
      // 直接提出の札が入札制限違反だけで外れた時の、 承認申請が通った札の件数 (#8321)。
      showSuccessToast({
        title: t('page:biddingIntraday.submit.requestSuccess', {
          count: summary.toast.count,
        }),
      })
    }
    // AC-BIN-015: 入札制限違反の明細が取れた分はモーダルを開いたまま表示し続ける。
    // それ以外 (全件成功、 または楽観ロック競合等 violation 以外の失敗のみ) は
    // 従来どおり閉じる。
    if (summary.violations.length === 0 && withdrawAtPassed.length === 0) {
      setSubmitModalOpen(false)
    }
  }

  return (
    <VStack align="stretch" gap={4}>
      {/* 発注主体 0 件ガード (AC-BIN-056)。 3 導線を disabled にするだけだと
          「押せない」 ことは伝わっても理由が伝わらないため、 modal 内の案内
          ([[IssuerSelector]]) と同じ文言をモーダルを開く前にも表示する。
          設定の不足 (押す前から分かる状態) なので status は info
          (UI 辞書 action-reason.md の区分 1)。 */}
      {noIssuer && <Alert status="info" title={noIssuerMessage} />}
      {/* 提出の結果 (一部の札だけ通った時、 または 1 件も通らず時間をおいても通らない失敗がある時。 AC-BIN-009)。 */}
      {submitFailure !== undefined &&
        isSameDay(submitFailure.targetDate, targetDate) && (
          <IntradaySubmitFailureBanner
            failures={submitFailure.failures}
            onDismiss={() => setSubmitFailure(undefined)}
            passedCount={submitFailure.passedCount}
          />
        )}
      {/* 一括作成の結果 (一部の札だけ作れた時。 AC-BIN-006b)。 */}
      {bulkResult !== undefined &&
        isSameDay(bulkResult.targetDate, targetDate) && (
          <Alert
            flexShrink={0}
            description={
              <>
                {bulkResult.gcExcluded > 0 && (
                  <Text>
                    {t('page:biddingIntraday.bulkCreate.result.gcExcluded', {
                      count: bulkResult.gcExcluded,
                    })}
                  </Text>
                )}
                {bulkResult.failed > 0 && (
                  <>
                    <Text>
                      {t('page:biddingIntraday.bulkCreate.result.failed', {
                        count: bulkResult.failed,
                      })}
                    </Text>
                    {bulkResult.reasons.map((reason) => (
                      <Text key={reason} whiteSpace="pre-line">
                        {reason}
                      </Text>
                    ))}
                    {bulkResult.hasUnknownReason && (
                      <Text>
                        {t('page:biddingIntraday.bulkCreate.result.retryLater')}
                      </Text>
                    )}
                  </>
                )}
              </>
            }
            status="error"
            title={t('page:biddingIntraday.bulkCreate.success', {
              count: bulkResult.created,
            })}
          >
            <AlertDismissButton onClick={() => setBulkResult(undefined)} />
          </Alert>
        )}
      {/* 一括板取消で受け付けなかった札 (AC-BIN-112)。 受渡日ごとの結果なので、 一括作成の結果と
          同じく受渡日を変えたら出さない。 次の一括板取消の結果で置き換える。 */}
      {bulkWithdraw.refused !== undefined &&
        isSameDay(bulkWithdraw.refused.targetDate, targetDate) && (
          <IntradayBulkWithdrawRefusedBanner
            onDismiss={bulkWithdraw.dismissRefused}
            refused={bulkWithdraw.refused}
          />
        )}
      {/* 予約送信案内バナー (AC-01/AC-14)。 市場未開場でも作成・提出は現行どおり
          可能なため、 導線をブロックせず案内のみ出す (PR #4752 の作成ガードは
          実装しない、 §4 UI/State)。 開場を跨いだら useNowByMinute の再評価で
          自動的に消える (AC-17)。
          TODO(#4917): spec 承認後に文言を確定する (現状は仮置き)。 */}
      {/* 受付前 (AC-BIN-089c) は予約送信もできないので、 予約送信案内ではなく
          「いつから受け付けるか」 を出す (受付前 ⊂ 未開場 なので排他)。 */}
      {beforeAcceptance ? (
        <Alert
          description={t('page:biddingIntraday.acceptance.bannerBody', {
            startTime: acceptanceStartTime,
          })}
          status="info"
          title={t('page:biddingIntraday.acceptance.bannerTitle')}
        />
      ) : (
        marketClosed && (
          <Alert
            description={t('page:biddingIntraday.reserved.bannerBody', {
              openTime: marketOpenTime,
            })}
            status="info"
            title={t('page:biddingIntraday.reserved.bannerTitle')}
          />
        )
      )}

      {/* 売買逆転 (価格交差) 検知バナー (AC-BIN-045/046)。 検知件数 > 0 でも提出は
          ブロックしない (spot AC-BSP-009 と同じ扱い。 意図的な両建てがありうるため)。
          検知対象プールは上の inversions 算出コメント参照。 */}
      <IntradayInversionAlert count={inversions.length} />

      {/* 承認フロー設定の取得失敗案内 (2026-09-16 決定)。 取得中は一瞬で解決するため
          Alert を出さず、 作成ボタンの非活性 (`isApprovalFlowUnavailable`) だけで表す
          (= BulkCreateModal の入札制限取得と同じ流儀)。 取得失敗の間は再読み込みを促す。 */}
      {isApprovalFlowScreenError && (
        <Alert
          description={t(
            'page:biddingIntraday.bidCreate.approvalFlowUnavailableBody',
          )}
          status="error"
          title={t(
            'page:biddingIntraday.bidCreate.approvalFlowUnavailableTitle',
          )}
        />
      )}

      {/* ========== 入札後のポジション chart (= accordion + 充足率 badge、 コマクリックで札作成) ========== */}
      {/* design intraday.html `.intraday-position-chart` (= `.ecl-accordion`)。 スクロールしても
          上端に固定しない (2026-10-08 デザイナー判断。 旧: position:sticky;top:0 で留めていた)。
          zIndex は後続の入札一覧 accordion より前面 (= viewbox からはみ出した tooltip が裏に隠れるのを
          防ぐ) なので残し、 効かせるために position:relative を持たせる。
          外枠 (border + radius-lg) は accordion 全体、 充足率の状態色は trigger のみ、 本文は白背景 +
          border-top で区切る (= design `.ecl-accordion` / `.ecl-accordion-content` 準拠)。

          **背景は角丸を持つ要素 (= AccordionItem) に載せる。** 以前は radius を持たない
          `AccordionRoot` に白を敷いていたが、 root は矩形なので丸角カードの**外側 4 隅**に
          白い直角がページ地 (`Surface.secondary` #f8f8f8) の上に出ていた
          (2026-08-25 デザイナー指摘「余計な背景が見えている」)。 本文が chart の裏を
          通り抜けても透けない役目は item の背景がそのまま引き継ぐ。 */}
      <AccordionRoot
        position="relative"
        zIndex={10}
        collapsible
        onValueChange={(e: { value: string[] }) => setPositionOpen(e.value)}
        value={positionOpen}
      >
        <AccordionItem
          borderWidth="1px"
          borderColor="Border.lowEmphasis"
          borderRadius="lg"
          backgroundColor="Surface.primary"
          value="position"
        >
          {/* paddingY が無いと chakra 既定の 8px になる。 design
              `bidding/intraday.html:270` の summary は
              `padding: var(--space-3) var(--space-4)` = 上下 12px なので明示する
              (スポット側の 2 タブとも高さが揃う)。

              **trigger の角丸は「上は常時 / 下は閉じている間だけ」。**
              chakra の accordion slotRecipe は `itemTrigger` に
              `border-radius: var(--accordion-radius)` (= DS の `radii.sm` = **2px**) を
              四隅へ既定で置く。 上 2 隅だけ lg に上書きすると、 閉じている時に
              trigger がカード下端を兼ねるので、 残った 2px の**不透明な** tint が
              item の 8px の弧を上塗りして角が四角く見える (2026-08-25 デザイナー指摘)。
              8080 も同じ症状を `bidding/spot.html:539-541` で直しているが、 8080 の
              chart は常時 `<details open>` なので下 2 隅は未対応のまま残っていた。

              既定を lg にして**開いた時だけ 0 に戻す**極性にしているのは、 万一
              `data-state` が取れなくても報告されたバグ状態 (= 閉) が正しくなる側に
              倒すため。 `borderRadius` の shorthand を使わず longhand ペアで書くのは、
              同一 style object 内の shorthand / longhand の emit 順が評価順に依存する
              ため (この PR の区切り線が黒くなっていたのと同じ罠)。

              tint 自体を trigger に置くのは 8080 `css/components.css:4897-4899` の
              `.cov-state-* > .ecl-accordion-trigger { background: ... }` 準拠。
              「8080 の trigger は background を持たない」 のは base の話で、
              状態色は trigger 側が正。 */}
          <AccordionItemTrigger
            borderTopRadius="lg"
            borderBottomRadius="lg"
            _open={{ borderBottomRadius: 'none' }}
            backgroundColor={accordionBg}
            paddingX={4}
            paddingY={3}
          >
            {/* 開閉標は header の**左端**。 DS `AccordionIconButton` を使う */}
            <AccordionDisclosureIcon />
            <HStack flex={1} gap={3}>
              <Text textStyle="accordionTitle" whiteSpace="nowrap">
                {t('page:biddingCommon.chart.positionAfter')}
              </Text>
              <Text textStyle="captionNormal" color="Text.midEmphasis">
                {areaLabel}
              </Text>
              <Spacer />
              {/* eslint-disable-next-line ebalance/no-adhoc-typography -- 充足率 badge md/semibold (design components.css:3785 .gap-coverage-badge = md/600 一致。SpotBidding 側の同型 2 箇所と同文言で統一、バッジ統合トラック defer) */}
              <Text
                color={badgeColor}
                fontSize="md"
                fontWeight="semibold"
                whiteSpace="nowrap"
              >
                {t('page:biddingCommon.chart.legendCoverage')}{' '}
                {shownCoverageRate !== undefined ? (
                  <>
                    <Text
                      as="strong"
                      fontSize="lg"
                      css={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {Math.round(shownCoverageRate)}%
                    </Text>{' '}
                    {/* 状態の語 (「買い過剰」「逆方向」等)。 色だけに意味を持たせない
                        (WCAG 1.4.1) ため、 design `cov-word` と同じく必ず併記する。 */}
                    <Text as="span" textStyle="captionNormal">
                      {coverage.coverageStateKey !== undefined
                        ? t(
                            `page:biddingCommon.coverageState.${coverage.coverageStateKey}`,
                          )
                        : ''}
                    </Text>{' '}
                    <Text
                      as="span"
                      textStyle="captionNormal"
                      color="Text.midEmphasis"
                      css={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {/* totalNeedMw<0 (= 当日ネット余剰) は負値を生表示せず、
                          「余剰」 prefix + 絶対値にする (cover は net 充当で余剰の売り充当
                          では負になるため、 余剰時は cover も絶対値表示。 coverageRate は
                          符号付き同士の除算で正になる)。 単位は AC-BIN-043 (2026-09-08 決定)
                          によりコマ別数量の単純合算 MW (MWh 換算なし)。 書式は
                          集計値なので小数 2 桁 (`formatMwSum`、 conventions.md「数値表示」)。 */}
                      (
                      {formatMwSum(
                        coverage.totalNeedMw < 0
                          ? Math.abs(coverage.totalCoverMw)
                          : coverage.totalCoverMw,
                      )}{' '}
                      /{' '}
                      {coverage.totalNeedMw < 0
                        ? `${t('page:biddingCommon.chart.coverageSurplusPrefix')} `
                        : ''}
                      {formatMwSum(Math.abs(coverage.totalNeedMw))} MW)
                    </Text>
                  </>
                ) : (
                  <Text as="span" color="Text.lowEmphasis" fontWeight="normal">
                    —
                  </Text>
                )}
              </Text>
            </HStack>
          </AccordionItemTrigger>
          {/* 本文は白背景 + 上境界線 (= design `.ecl-accordion-content` border-top: --border-low)。
              paddingX は design `.ecl-accordion-content` の space-4 (16px)。 outline variant は
              content に px を付けないため明示し、 Y 軸 "MW" ラベルが左枠に張り付くのを防ぐ。
              overflow:visible は Chakra v3 accordion itemContent recipe の overflow:hidden を
              上書きし、 chart より高い tooltip が本文の枠で切れるのを防ぐ (= v2 の `.chakra-collapse`
              セレクタは v3 で消滅したため、 content の prop で直接解除する)。 */}
          {/* paddingBottom が無く下だけ余白 0 だった (デザイナー指摘 2026-08-14
              「accordion の内部余白がほぼない」)。 design
              `bidding/intraday.html:276` の本文は `padding: var(--space-3)` = 12px
              なので上下を 12px で揃える。 左右 16px は既存値のまま据え置く。 */}
          <AccordionItemContent
            overflow="visible"
            paddingTop={3}
            paddingBottom={3}
            // 色なしの shorthand は borderColor より後勝ちして currentColor (= 黒) に
            // なるため longhand で書く (2026-08-25 ブラウザ実測)。 8080 は
            // `.ecl-accordion-content { border-top: 1px solid var(--border-low) }`。
            borderTopWidth="1px"
            borderTopColor="Border.lowEmphasis"
            borderBottomRadius="lg"
            backgroundColor="Surface.primary"
            paddingX={4}
          >
            {isSingleAreaSelected ? (
              <PositionChart
                colors={INTRADAY_POSITION_COLORS}
                gcBoundarySlotIndex={gcSlotIndex}
                gcSlotIndex={gcLineIndex}
                needUnavailable={needUnavailable}
                onSlotClick={openCreateForKoma}
                slots={positions}
              />
            ) : (
              /* AC-BIN-012b: 「すべて」 の間は accordion ごと畳むが、 手動で開いたときに
                 空白にせず理由を示す (= 描画されないのが不具合に見えないようにする)。
                 高さ・背景は PositionChart 自身の空状態 (slots 0 件) と同じ流儀に揃える
                 (chart 本体 + 凡例行 = POSITION_CHART_PLACEHOLDER_HEIGHT)。 */
              <VStack
                align="center"
                justify="center"
                gap={1}
                height={`${POSITION_CHART_PLACEHOLDER_HEIGHT}px`}
                textAlign="center"
                borderRadius="md"
                backgroundColor="Surface.secondary"
                paddingX={6}
              >
                <Text color="Text.midEmphasis">
                  {t('page:biddingIntraday.positionChart.areaAllGuide')}
                </Text>
              </VStack>
            )}
          </AccordionItemContent>
        </AccordionItem>
      </AccordionRoot>

      {/* ========== 板情報・連系線空容量パネル (AC-BIN-010 / AC-BIN-011) ==========
          design `bidding/intraday.html:339-364`(`#intraday-board-panel`)準拠。
          板発注タブ (`MarketBoardTab`) と同じ板 + 連系線パネルを共通コンポーネント
          (`IntradayBoardInterconPanel`) 経由で参照窓として置く。 このタブは #7573/#7548 で
          KPI カード・48コマリボンを撤去して入札一覧の縦を確保した経緯があるため、
          板発注タブと異なり**常時展開にせず折りたたみ (既定: 閉)** で置く
          (板の全体像は板発注タブが担い、 ここは札を作りながら気配と空容量を確認するための
          参照窓という位置づけ)。 対象コマは `firstOpenKoma` (= まだ入札できる最も早いコマ、
          コマフィルタ未実装のための design フォールバック、 上の宣言部の注記を参照)。 */}
      <AccordionRoot
        collapsible
        onValueChange={(e: { value: string[] }) => {
          if (
            e.value.includes('boardPanel') &&
            !boardPanelOpen.includes('boardPanel')
          ) {
            markBoardStale()
          }
          setBoardPanelOpen(e.value)
        }}
        value={boardPanelOpen}
      >
        <AccordionItem
          borderWidth="1px"
          borderColor="Border.lowEmphasis"
          borderRadius="lg"
          backgroundColor="Surface.primary"
          value="boardPanel"
        >
          <AccordionItemTrigger
            borderTopRadius="lg"
            borderBottomRadius="lg"
            _open={{ borderBottomRadius: 'none' }}
            paddingX={4}
            paddingY={3}
          >
            {/* 開閉標は header の左端 (= 他 accordion と同じ流儀)。 */}
            <AccordionDisclosureIcon />
            <HStack flex={1} gap={3}>
              <Text textStyle="accordionTitle" whiteSpace="nowrap">
                {t('page:biddingIntraday.bidCreate.boardPanelTitle')}
              </Text>
              <Text textStyle="captionNormal" color="Text.midEmphasis">
                {boardTargetKoma !== undefined
                  ? t('page:biddingIntraday.market.komaTitle', {
                      koma: boardTargetKoma,
                    })
                  : '—'}
              </Text>
            </HStack>
          </AccordionItemTrigger>
          <AccordionItemContent
            overflow="visible"
            paddingTop={3}
            paddingBottom={3}
            borderTopWidth="1px"
            borderTopColor="Border.lowEmphasis"
            borderBottomRadius="lg"
            backgroundColor="Surface.primary"
            paddingX={4}
          >
            <IntradayBoardInterconPanel
              boardRows={boardRows}
              interconLines={boardInterconLines}
              interconUpdatedAt={boardInterconUpdatedAt}
              isErrorBook={isErrorBoardBook}
              isErrorIntercon={isErrorBoardIntercon}
              isLoadingBook={isLoadingBoardBook}
              isLoadingIntercon={isLoadingBoardIntercon}
              streamStopped={boardStreamStopped}
              totalBuyVolume={boardTotalBuyVolume}
              totalSellVolume={boardTotalSellVolume}
            />
          </AccordionItemContent>
        </AccordionItem>
      </AccordionRoot>

      {/* ========== 入札一覧 accordion ==========
          design intraday.html は page スクロール方針 (= `#tab-zaraba` overflow:visible)。
          内部 flex/overflow:hidden の高さ制約は持たず、 内容が自然に伸びてページがスクロールする
          (= spot.html と同方針、 旧 内部スクロールは撤去)。 */}
      <AccordionRoot collapsible defaultValue={['bidList']}>
        <AccordionItem
          // 切り取らない。 本文の絞り込みの FilterMultiSelect は候補をその場 (absolute) に開くので、
          // 一覧が短い時にカードで切り取ると候補の下が切れる。 角丸は下端の表の箱が持つ。
          overflow="visible"
          borderWidth="1px"
          borderColor="Border.lowEmphasis"
          // lg = 8px。 同画面のポジション chart accordion と 8080 `.ecl-accordion`
          // (radius-lg) に揃える (2026-08-31 アコーディオン基準統一)。
          borderRadius="lg"
          // カードの白は**角丸を持つこの要素**に載せる (8080 `.ecl-accordion`)。
          // trigger 側に置くと、 recipe 既定の 2px 角丸の塗りが item の 6px の弧を
          // 上塗りして角が欠ける (2026-08-25 デザイナー指摘)。
          backgroundColor="Surface.primary"
          value="bidList"
        >
          {/* 見出しの行。 右端に入札一覧の操作 (入札追加▽ / 提出 / ⋯) を置く (2026-10-26 デザイナー
              「入札追加などのボタン群はアコーディオンの header 右端に配置移動」)。 操作は trigger
              (= <button>) の中に入れず兄弟に置く (button の入れ子を避ける。 SpotPrice の日送りと同じ考え方)。
              一覧を閉じていても札の追加・提出はできる。 */}
          <Flex alignItems="center">
            <AccordionItemTrigger
              flex={1}
              flexShrink={0}
              minWidth={0}
              // 区切り線は trigger ではなく content 側に持つ (8080 `.ecl-accordion-trigger` は
              // border を持たない)。 trigger 側に置くと**閉じている間も線が残る**
              // (2026-08-25 デザイナー指摘)。
              //
              // background / hover は持たせない。 8080 は
              // `bidding/intraday.html:58` で
              // `#intraday-bid-list-accordion > summary.ecl-accordion-trigger:hover
              //  { background: transparent }` と**明示的に hover を殺している**
              // (理由: trigger 内の button の hover が全幅の塗りに覆い隠されるため)。
              // 実装の trigger も radio group 10 個 + リセットボタン + select を
              // 内包しているので同じ理由が成立する。 透明にすることで recipe 既定の
              // 2px 角丸は塗る面を持たなくなり、 角の形は item の宣言だけで決まる。
              paddingX={4}
              paddingY={3}
            >
              {/* 開閉標は header の**左端**。 DS `AccordionIconButton` を使う */}
              <AccordionDisclosureIcon />
              <HStack
                flexWrap="wrap"
                flex={1}
                gap={3}
                width="100%"
                minWidth={0}
              >
                <Text textStyle="accordionTitle" whiteSpace="nowrap">
                  {t('page:biddingSpot.list.bidListTitle')}{' '}
                  <Text
                    as="span"
                    textStyle="captionNormal"
                    color="Text.lowEmphasis"
                    css={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {t('page:biddingSpot.list.countSuffix', {
                      count: tableBids.length,
                    })}
                  </Text>
                </Text>
              </HStack>
            </AccordionItemTrigger>
            {/* 入札一覧の操作 (入札追加▽ / 提出 / ⋯)。 手順ステップ表示 (入札追加 → 承認確認 →
                提出完了) は 2026-10-05 の PdM 判断で廃止した (#8312、 spec §機能詳細 8・旧 AC-BIN-094 は
                欠番)。 札ごとの進み具合はステータス列、 承認の状態は承認状態バナーで読める。 */}
            <Flex alignItems="center" flexShrink={0} gap={3} paddingRight={4}>
              {/* 提出できる下書きが絞り込みで全て隠れている間の押せない理由
                  (conventions.md「操作できない時のボタンの扱い」 2)。 */}
              {submitDisabledCause === 'hiddenByFilter' && (
                <ActionReasonText>
                  {t('page:biddingIntraday.submit.hiddenByFilter')}
                </ActionReasonText>
              )}
              <HStack gap={2}>
                {/* 入札追加▽ (= design bid-create-menu: 個別 / 一括 / CSV) */}
                {/* スポット入札の同ボタンに揃える (2026-08-25 Des 指摘)。
                        design `spot.html` の `.ecl-split-btn` 準拠で、 **主ボタン =
                        個別作成を即実行**、 右の caret からその他の作成方法を開く。
                        3 項目を対等に並べる dropdown ではない。
                        従来は「＋」「▾」 を文字で書いた単一ボタンだったため、
                        アイコン・寸法・区切り線がスポット側と揃っていなかった。
                        区切り線は caret の左 border (文字色の 25%) + `marginLeft="-1px"` で主ボタンの右 border に
                        重ねて 1 本にする (design/ui-feasibility/components/split-button.md「dev 実装の現状」。
                        計画作成 = pages/ProcurementPlanList と同じ書き方)。 */}
                <HStack gap={0}>
                  {/* [AC-BIN-056] 発注主体が 1件も登録されていないと押しても
                          必ず失敗するため、 他の disabled 条件と同じ枠で塞ぐ。
                          理由は上部の Alert で示す (UI 辞書 action-reason.md の区分 1)。
                          読み取り専用 (AC-WFW-014) の理由だけは例外で、 非活性の間だけ
                          ButtonTooltip で包む (理由が無い時も span の包みは残り、 吹き出しだけ出ない)。 */}
                  <ButtonTooltip
                    content={memberBidDisabledReason ?? readonlyReason}
                  >
                    <TertiaryButton
                      borderRightRadius={0}
                      // 過去日は全コマ締切済 = 作成できるコマが無いので無効化する
                      // (= 有効コマが無いまま作成 modal を開かない)。
                      // 受付前 (AC-BIN-089c) も同様に作成導線を閉じる。
                      // 承認フロー設定の取得中・取得失敗の間も作成させない
                      // (`isApprovalFlowUnavailable` 参照、 2026-09-16 決定)。
                      disabled={!canCreateBid}
                      onClick={() => {
                        if (firstOpenKoma === undefined) return
                        openCreateForKoma(firstOpenKoma)
                      }}
                      size="md"
                    >
                      <FaPlusRegular boxSize={iconSize.labeledButton} />
                      {t('page:biddingIntraday.modal.createTitle')}
                    </TertiaryButton>
                  </ButtonTooltip>
                  <ButtonTooltip
                    content={memberBidDisabledReason ?? readonlyReason}
                  >
                    <RichActionMenu
                      ariaLabel={t(
                        'page:biddingIntraday.bidCreateMenu.moreLabel',
                      )}
                      items={[
                        {
                          value: 'bulk',
                          title: t('page:biddingIntraday.bidCreateMenu.bulk'),
                          description: t(
                            'page:biddingIntraday.bidCreateMenu.bulkDesc',
                          ),
                          icon: <FaBoltRegular boxSize={iconSize.gridInline} />,
                          // 対象コマが無いと preview も 0 件になるため無効。
                          // 受付前 (AC-BIN-089c) も一括作成させない。
                          // [AC-BIN-056] 発注主体 0 件も同様に塞ぐ。
                          // 承認フロー設定の取得中・取得失敗の間も無効
                          // (`isApprovalFlowUnavailable` 参照、 2026-09-16 決定)。
                          disabled:
                            beforeAcceptance ||
                            firstOpenKoma === undefined ||
                            noIssuer ||
                            isApprovalFlowUnavailable ||
                            memberBidDisabledReason !== undefined,
                          onClick: () => {
                            if (memberBidDisabledReason !== undefined) return
                            setBulkOpen(true)
                          },
                        },
                        {
                          value: 'csv',
                          title: t('page:biddingIntraday.bidCreateMenu.csv'),
                          description: t(
                            'page:biddingIntraday.bidCreateMenu.csvDesc',
                          ),
                          icon: (
                            <FaArrowUpFromBracketRegular
                              boxSize={iconSize.gridInline}
                            />
                          ),
                          // CSV 取込は、 選択中の受渡日が受付前 (AC-BIN-089c)
                          // でも操作できる。 受渡日は CSV の行ごとに決まり、
                          // 受付開始前の受渡日の行は取込側が行エラーにする
                          // (intraday-csv-import.md AC-BCI-011a)。
                          // [AC-BIN-056] 発注主体 0 件は塞ぐ (取込画面・
                          // CSV 取込処理そのものは対象外。 ここはボタン側の
                          // 導線制御のみ)。
                          disabled:
                            noIssuer || memberBidDisabledReason !== undefined,
                          // spec intraday-csv-import.md §6: 子ページへ遷移する
                          // (モーダルではない)。 選択中の受渡日は引き継がない
                          // (AC-BIN-102 / AC-BCI-002) ので受渡日の query は付けない
                          // (付けるのは戻り先だけ)。
                          onClick: () => {
                            if (memberBidDisabledReason !== undefined) return
                            void navigate(csvImportLink)
                          },
                        },
                      ]}
                      trigger={
                        <TertiaryButton
                          marginLeft="-1px"
                          borderLeftWidth="1px"
                          borderLeftColor="color-mix(in srgb, currentColor 25%, transparent)"
                          borderLeftRadius={0}
                          aria-label={t(
                            'page:biddingIntraday.bidCreateMenu.moreLabel',
                          )}
                          disabled={
                            isReadonly || memberBidDisabledReason !== undefined
                          }
                          size="md"
                          title={t(
                            'page:biddingIntraday.bidCreateMenu.moreLabel',
                          )}
                        >
                          {/* caret は装飾 (開閉の読み上げ名は aria-label 側が持つ)。
                              8080 は 14px の svg chevron (`.ecl-split-btn-caret svg`)。
                              [DS差異] design/CLAUDE.md の「caret は Solid」は
                              `@enechain/ecloud-icons` に FaChevronDownSolid が無いため
                              満たせず、 Regular を採る (MasterTableActionBar と同判断)。 */}
                          <FaChevronDownRegular
                            boxSize={iconSize.labeledButton}
                          />
                        </TertiaryButton>
                      }
                    />
                  </ButtonTooltip>
                </HStack>
                {/* JEPX 提出 (= design wizard-submit-btn)。 未送信注文を SubmitConfirmModal
                        経由でまとめて SUBMIT する主導線。 件数は実提出対象 (submitSummary) で出す。
                        提出対象が 0 件のときは非活性 (= design `intraday.html`
                        `_refreshIntradaySubmitBtn` の `submittableCount === 0` と同条件)。
                        受付前 (AC-BIN-089c) も非活性にする。 作成導線は塞いであるが、
                        受付開始ルール導入前に作られた将来日の下書きが残っていると提出
                        だけ通ってしまい、 backend が BEFORE_ACCEPTANCE_START で拒否した
                        結果が件数だけの partial トーストに畳まれて理由が伝わらないため。
                        承認フロー設定の取得中・取得失敗の間も非活性にする (2026-09-16 決定、
                        `isApprovalFlowUnavailable` 参照): 対象ごとの REQUEST/SUBMIT 判定
                        (`actionOf`) がフォールバック (AUTO) の間は誤って SUBMIT を選びうる。 */}
                <ButtonTooltip content={readonlyReason}>
                  <PrimaryButton
                    disabled={submitDisabledCause !== undefined}
                    loading={isSubmitting || isRefreshingSuspension}
                    loadingText={
                      isSubmitting
                        ? t('page:biddingSpot.wizard.submitting')
                        : undefined
                    }
                    onClick={() => {
                      // AC-BIN-015: 前回の提出試行で残った違反明細を持ち越さない
                      // (#8317: 同じ試行単位で持つ送信済み件数もあわせて戻す)。
                      setBidLimitViolations([])
                      setBidLimitSentCount(0)
                      setBidLimitReservedOpenTime(undefined)
                      setWithdrawAtPassedBids([])
                      setRequestWithdrawAtBlockedIds(new Set())
                      setSubmitResultOpen(false)
                      if (!productsEnabled) {
                        setSubmitModalOpen(true)
                        return
                      }
                      // AC-BIN-034a: 警告を提出時点の取引状態で出すため、 取り直しが届いてから
                      // (失敗しても) 開く。
                      setIsRefreshingSuspension(true)
                      void refetchProducts().finally(() => {
                        setIsRefreshingSuspension(false)
                        setSubmitModalOpen(true)
                      })
                    }}
                  >
                    {t('page:biddingIntraday.submit.button', {
                      count: submitSummary.count,
                    })}
                  </PrimaryButton>
                </ButtonTooltip>
                <MenuRoot
                  // MenuRoot の props も型解決で collapse するため、 引数の型を明示する。
                  onHighlightChange={(details: {
                    highlightedValue: string | null
                  }) => {
                    setMoreMenuHighlighted(details.highlightedValue)
                  }}
                >
                  <MenuTrigger
                    aria-label={t('page:biddingSpot.menu.moreLabel')}
                    title={t('page:biddingSpot.menu.moreLabel')}
                  >
                    <TertiaryButton as="span">
                      <FaEllipsisRegular boxSize={iconSize.labeledButton} />
                    </TertiaryButton>
                  </MenuTrigger>
                  <MenuPositioner>
                    <MenuContent>
                      <MenuItem
                        onClick={(e: MouseEvent) =>
                          openOrNavigate(
                            navigate,
                            routePaths.adminBidLimits.path,
                            e,
                          )
                        }
                        value="bid-limits"
                      >
                        {t('page:biddingSpot.menu.bidLimits')}
                      </MenuItem>
                      <MenuItem
                        onClick={(e: MouseEvent) =>
                          openOrNavigate(
                            navigate,
                            routePaths.adminApprovalFlow.path,
                            e,
                          )
                        }
                        value="approval-flow"
                      >
                        {t('page:biddingSpot.menu.approvalFlow')}
                      </MenuItem>
                      <MenuItem
                        onSelect={() =>
                          appToaster.create({
                            type: 'info',
                            title: `${t('page:biddingIntraday.menu.history')} (mock)`,
                          })
                        }
                        value="history"
                      >
                        {t('page:biddingIntraday.menu.history')}
                      </MenuItem>
                      <MenuItem
                        onSelect={() =>
                          appToaster.create({
                            type: 'info',
                            title: `${t('page:biddingIntraday.executionResult.csvDownload')} (mock)`,
                          })
                        }
                        value="csv-download"
                      >
                        {t('page:biddingIntraday.executionResult.csvDownload')}
                      </MenuItem>
                      <IntradayBulkWithdrawMenuItem
                        bulkWithdraw={bulkWithdraw}
                        highlighted={
                          moreMenuHighlighted ===
                          INTRADAY_BULK_WITHDRAW_MENU_VALUE
                        }
                      />
                    </MenuContent>
                  </MenuPositioner>
                </MenuRoot>
              </HStack>
            </Flex>
          </Flex>
          <AccordionItemContent
            // recipe の overflow:hidden を外す (絞り込みの候補を切らない)。 同画面のポジション・板の
            // accordion と同じ。 content に data-state が付かない (ecloud 0.14 / chakra 3.29) ので
            // `_open` では当たらない (2026-10-27 実測)
            overflow="visible"
            padding={0}
            // 8080 `.ecl-accordion-content { border-top: 1px solid var(--border-low) }`。
            // **content 側**に置くのが正で、 trigger 側に置くと閉じている間も線が残る。
            // 色なしの shorthand (`borderTop="1px solid"`) は borderColor より後勝ちして
            // currentColor (= 黒) になるため longhand で書く (2026-08-25 実測)。
            borderTopWidth="1px"
            borderTopColor="Border.lowEmphasis"
          >
            {/* ========== 入札一覧の絞り込み (開閉式) ==========
                UI 辞書 toolbar-collapsible.md「開閉式」。 入札一覧の表のすぐ上に置く
                (2026-10-26 デザイナー「入札一覧の table 上に配置」)。 外側のアコーディオンが枠を持つので
                枠を付けず (`framed={false}`)、 表との間は区切り線にする。 閉じている間は今の条件を要約で読める。
                件数・クリアの対象は一覧固有の条件 (ステータス・ワークフロー・コマ・売買逆転のみ)。
                売買区分は全体フィルター (AC-BIN-102) の値なので数えず、 クリアでも戻さない (AC-BIN-132)。
                エリアは画面の最上部の全体フィルター (IntradayFilterToolbar) にある。 */}
            <Box borderBottomWidth="1px" borderBottomColor="Border.lowEmphasis">
              <CollapsibleFilterToolbar
                framed={false}
                modifiedCount={modifiedFilterCount}
                onClear={resetFilters}
                storageKey="intradayBidCreate"
                summary={filterSummary}
              >
                <FilterGrid items={filterGridItems} />
              </CollapsibleFilterToolbar>
            </Box>

            {/* 入札一覧テーブル。 縦は page スクロールに委譲し、 横のみ table-scroll で
                委譲する (= spot.html と同方針、 二重スクロール回避)。 カードは切り取らないので、
                下端の角丸はこの箱が持つ (選択中の行の地色がカードの角からはみ出さない)。 半径は
                カードの枠 1px の内側の弧に合わせる (lg のままだと行と枠の間に白い弧が出る)。 */}
            <Box overflowX="auto" borderBottomRadius="calc({radii.lg} - 1px)">
              <IntradayBidListAreaGate
                state={noIssuer ? undefined : areaEmptyState}
              >
                <IntradayBidTable
                  approvalNoticeOf={approvalNoticeOf}
                  emptyState={
                    // AC-BIN-016: 受渡日の札が 0 件 (一覧の取得が済んでから。 取得中は
                    // 0 件と断定しない) の時だけ「入札がありません」 を出す。 絞り込みの 0 件は
                    // `allBids` (絞り込み前) に札があるので対象外。 作成の導線が開いている間は札作成への
                    // 誘導を足し、 閉じている間 (読取専用・発注主体なし等) は押せないボタンへ
                    // 誘導せず事実だけ出す。 受付前 (AC-BIN-089c) は従来の文言のまま。
                    listData !== undefined &&
                    allBids.length === 0 &&
                    !beforeAcceptance
                      ? canCreateBid
                        ? 'noBidsWithGuide'
                        : 'noBids'
                      : 'filtered'
                  }
                  inversionBidIds={inversionBidIds}
                  isReadonly={isReadonly}
                  isReturningToDraft={returnToDraft.isReturning}
                  onApprove={handleApprove}
                  onDelete={deleteConfirm.open}
                  onReject={handleReject}
                  onReturnToDraft={returnToDraft.returnToDraft}
                  onRowClick={handleRowClick}
                  onWithdraw={withdrawConfirm.open}
                  rows={tableBids}
                  suspendedTimeCodes={suspendedTimeCodes}
                />
              </IntradayBidListAreaGate>
            </Box>
          </AccordionItemContent>
        </AccordionItem>
      </AccordionRoot>

      {/* 入札追加 modal (= コマクリックで開く、 prefill 付き、 design intraday に項目一致) */}
      {isTargetDateReady && (
        <IntradayBidCreateModal
          defaultValues={createPrefill}
          deliveryDate={targetDate}
          feeState={feeState}
          isOpen={createOpen && memberBidDisabledReason === undefined}
          onClose={() => setCreateOpen(false)}
          onSubmit={handleCreateSubmit}
          // 承認モードを確定できない間 (取得中・取得失敗) は案内を出さない
          // (= 作成導線自体が非活性で、 取得できない旨は上部の Alert が伝える)。
          resolveApprovalMode={
            isApprovalFlowUnavailable ? undefined : resolveApprovalModeForArea
          }
        />
      )}

      {/* 一括作成 modal (AC-BIN-006b、 design `#intraday-bulk-add-modal`)。
          コマ範囲 × エリア × 売/買 × 固定価格 × コマあたり MW → プレビュー → 確定。 */}
      {isTargetDateReady && (
        <IntradayBulkCreateModal
          defaultAreaCode={singleSelectedArea}
          deliveryDate={targetDate}
          existingBids={bulkExistingBids}
          feeState={feeState}
          gcSlotIndex={gcSlotIndex}
          isOpen={bulkOpen && memberBidDisabledReason === undefined}
          isSubmitting={isBulkCreating}
          onClose={() => setBulkOpen(false)}
          onSubmit={handleBulkCreateSubmit}
          // 個別作成モーダルと同じ扱い (取得中・取得失敗の間は案内を出さない)。
          resolveApprovalMode={
            isApprovalFlowUnavailable ? undefined : resolveApprovalModeForArea
          }
        />
      )}

      {/* JEPX 提出確認 modal (= wizard 最終 step の提出で開く、 spot と共通)。
          AC-060: 提出後の取消の期限は確認項目の「取消」 の行 (#9586)。 JEPX 制度上限 (AC-BIN-038) を
          満たさない札が残っていれば違反ブロック + 提出ボタン非活性 (AC-077 と同一文言)。
          テナント設定の入札制限 (AC-BIN-015 / AC-BDL-014) は、 承認申請になる札の違反だけを
          保存済み設定との事前チェック (preCheckLimitSection) で提出前に出して確定を止め、
          直接提出の札は backend が拒否した札の件数・明細を RPC 由来の節
          (bidLimitViolationSection) で表示する。 */}
      {/* 提出確認 modal (AC-BIN-009): 提出対象の承認モードが全件「要承認」の時だけ
          承認申請確認モーダル (title/confirmLabel/body/submittingLabel を要承認向けに
          差し替え) へ出し分ける。 それ以外 (承認なし / 自動 / エリア混在) はこれまでの
          直接提出確認 (既定文言) のまま — 出し分けの判定は `submitRequiresApproval`
          (上のコメント参照)。 エリア別内訳は両モードで表示し、 各行に提出対象の札が持つ
          受渡契約コードを併記する (AC-BIN-009 鉄則1。 札が持つ値で、 持たない札は「—」)。 */}
      <SubmitConfirmModal
        areaBreakdown={submitAreaBreakdown.map((row) => ({
          areaLabel: row.areaLabel,
          count: row.count,
          buyQuantity: row.buyMw,
          sellQuantity: row.sellMw,
          deliveryContractCodeText: formatAreaDeliveryContractCodes(row, t),
        }))}
        bidCount={submitSummary.count}
        // 冒頭文だけを出す (AC-BIN-060)。 提出の時は既定文言 (marketLabel で「時間前」)。
        // 取消の期限・承認フロー・送信のタイミングは確認項目の行に出す (#9586)。
        body={
          submitRequiresApproval
            ? t('page:biddingCommon.submitConfirm.requestDescription')
            : undefined
        }
        confirmDisabledReason={
          !submitResultOpen && isRequestWithdrawAtBlocked
            ? t('page:biddingIntraday.withdrawAtPassed.requestBlockedReason')
            : undefined
        }
        confirmItems={{
          market: 'intraday',
          approvalModes: submitApprovalModes,
          send: submitSendTiming,
          // 市場未開場中は予約になる (AC-BIN-089)。 送信の時刻は「JEPX への送信」 の値で
          // 示し、 別の注記は置かない (AC-BIN-060。 旧: 予約送信の注記 = AC-03)。
          reservedOpenAt: marketClosed ? marketOpenTime : undefined,
        }}
        confirmLabel={
          submitRequiresApproval
            ? t('page:biddingIntraday.submit.requestConfirm')
            : undefined
        }
        detailLines={submitFormulaDetailLines}
        excludedByFilterCount={excludedByFilterCount}
        hasViolation={
          hasJepxViolation ||
          hasBidLimitViolation ||
          (!submitResultOpen && isRequestWithdrawAtBlocked) ||
          (!submitResultOpen &&
            submitEligibleTargets.length === 0 &&
            submitWithdrawAtPassedBids.length > 0)
        }
        isOpen={submitModalOpen}
        isSubmitting={isSubmitting}
        leadingViolationSections={[submitViolationSections[0]]}
        marketLabel={t('page:biddingIntraday.market.label')}
        onClose={() => {
          setSubmitModalOpen(false)
          setSubmitResultOpen(false)
        }}
        onConfirm={() => {
          void handleSubmitToJepx()
        }}
        result={
          submitResultOpen
            ? {
                title: t(
                  'page:biddingIntraday.withdrawAtPassed.submittedTitle',
                ),
              }
            : undefined
        }
        submittingLabel={
          submitRequiresApproval
            ? t('page:biddingIntraday.submit.requesting')
            : undefined
        }
        title={
          submitRequiresApproval
            ? t('page:biddingIntraday.submit.requestModalTitle')
            : undefined
        }
        totalBuyQuantity={submitSummary.buyMw}
        totalSellQuantity={submitSummary.sellMw}
        violationSections={submitViolationSections.slice(1)}
        warningSections={submitWarningSections}
      />

      {/* 取消確認 modal (AC-008)。 板情報の自社入札と共通 (#10110)。 */}
      <IntradayWithdrawConfirmDialog confirm={withdrawConfirm} />
      {/* 入札中の札の一括板取消の確認 (AC-BIN-112)。 ⋯メニューから開く。 */}
      <IntradayBulkWithdrawConfirmDialog bulkWithdraw={bulkWithdraw} />
      <IntradayDeleteConfirmDialog confirm={deleteConfirm} />

      {/* 承認確認 (AC-BIN-095 / AC-BIN-097) + 差戻し (AC-BIN-095 / AC-BIN-053)。
          一覧の操作列「承認する」「差戻し」から開く。 組み立ては詳細モーダルと共通。 */}
      <IntradayApprovalDialogs decision={approvalDecision} />

      {/* 詳細・編集 modal (AC-007)。 行クリックで開き、 監査情報 + 状態遷移 (取下含む) を提供する。 */}
      {detailOrder !== undefined && (
        <IntradayOrderDetailModal
          row={detailOrder}
          feeState={feeState}
          isOpen
          onClose={() => setDetailOrder(undefined)}
          orders={allOrders}
        />
      )}
    </VStack>
  )
}
